import React, { Component } from 'react';
import {
  Autocomplete,
  Backdrop,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Tab,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Typography
} from '@mui/material';
import moment from 'moment';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import withRouter from '../../withRouter.js';
import Credentials, {
  currentTenant,
  thousandsdisplay,
  userid,
  financialyearstart,
  financialyearend,
  currency,
  clientname,
  ccity,
  ccountry,
  clienttz
} from '../../Common/Credentials.js';
import { Auth_Proto, Auth_Domain, Auth_Port } from '../../Component/consts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { spin } from '../../Common/NewLoader.jsx';
import Footer from '../../Component/Footer.js';
import Header from '../../Component/Header.js';
import Menu from '../../Component/Menu.js';
import NewJournalService from '../NewJournalService.js';
import NewReportService from '../NewReportService.js';
import PlComponent from './PlComponent.js';
import './table.css';

const CASH_FLOW_LINE_ITEMS = [
  // Section 1: Operating
  { type: 'header', label: 'CASH FLOWS FROM OPERATING ACTIVITIES' },
  { key: 'netProfitLoss', label: 'Net profit / (loss) for the year', note: null },
  { key: 'depreciation', label: 'Depreciation of fixed assets', note: '3' },
  { key: 'financeChargesAdj', label: 'Finance charges adjustment', note: null },
  { key: 'endOfServiceAdj', label: 'End-of-service / non-current liability adjustment as presented', note: null },
  { key: 'cashFlowBeforeWC', label: 'Cash flow before working-capital changes', note: null, isSubtotal: true },
  { key: 'accountsReceivableMovement', label: 'Accounts receivable movement', note: '5' },
  { key: 'inventoryMovement', label: 'Inventory movement', note: '6' },
  { key: 'otherDebitBalancesMovement', label: 'Other debit balances movement', note: '7' },
  { key: 'dueFromRelatedMovement', label: 'Due from related parties movement', note: null },
  { key: 'accountsPayableMovement', label: 'Accounts payable movement', note: '8' },
  { key: 'otherCreditBalancesMovement', label: 'Other credit balances movement', note: '9' },
  { key: 'dueToRelatedMovement', label: 'Due to related parties movement', note: null },
  { key: 'establishmentExpensesMovement', label: 'Establishment / pre-operating expenses movement', note: null },
  { key: 'netCashOperating', label: 'NET CASH FROM OPERATING ACTIVITIES', note: null, isTotal: true },

  // Section 2: Investing
  { type: 'divider' },
  { type: 'header', label: 'CASH FLOWS FROM INVESTING ACTIVITIES' },
  { key: 'acquisitionFixedAssets', label: 'Acquisition of fixed assets', note: '3' },
  { key: 'netCashInvesting', label: 'NET CASH FROM INVESTING ACTIVITIES', note: null, isTotal: true },

  // Section 3: Financing
  { type: 'divider' },
  { type: 'header', label: 'CASH FLOWS FROM FINANCING ACTIVITIES' },
  { key: 'capitalContributions', label: 'Capital contributions', note: '10' },
  { key: 'partnerCurrentChanges', label: 'Changes in shareholders / partner current', note: null },
  { key: 'loansMovements', label: 'Loans / financing movements', note: null },
  { key: 'financeChargesPaid', label: 'Finance charges paid', note: null },
  { key: 'netCashFinancing', label: 'NET CASH FROM FINANCING ACTIVITIES', note: null, isTotal: true },

  // Section 4: Cash Reconciliation
  { type: 'divider' },
  { key: 'netCashIncrease', label: 'NET INCREASE / (DECREASE) IN CASH', note: null, isTotal: true },
  { key: 'cashBeginning', label: 'Cash and cash equivalents – beginning', note: '4' },
  { key: 'cashEndCF', label: 'Cash and cash equivalents – end (cash-flow statement)', note: '4', isDoubleUnderline: true },
  { key: 'cashEndFP', label: 'Cash / bank – end (financial position)', note: '4' },
  { key: 'variance', label: 'CASH-FLOW VS FINANCIAL-POSITION VARIANCE', note: null, isVariance: true }
];

class BalanceSheetStatement extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      loader1: true,
      fylist: [],
      selectedFy: null,
      priorFy: null,
      fyfrom: null,
      fyto: null,
      priorFyfrom: null,
      priorFyto: null,
      activeTab: 0,

      // Current Year Data
      currAssets: [],
      currLiabilities: [],
      currPl: { debitexcess: 0, creditexcess: 0, netProfit: 0 },
      currProfitDist: null,
      currPartnerCurrent: null,

      // Prior Year Data
      priorAssets: [],
      priorLiabilities: [],
      priorPl: { debitexcess: 0, creditexcess: 0, netProfit: 0 },
      priorProfitDist: null,
      priorPartnerCurrent: null,

      // Two Years Prior Data (for dynamic prior cash flow computation)
      twoYearsPriorAssets: [],
      twoYearsPriorLiabilities: [],
      twoYearsPriorPl: { debitexcess: 0, creditexcess: 0, netProfit: 0 },
      twoYearsPriorProfitDist: null,
      twoYearsPriorPartnerCurrent: null,
      twoYearsPriorFy: null,

      // Equity Allocations (Share Capital setup)
      equityAllocations: [],

      activeNoteAnchor: null,

      // Cash Flow View Mode: 'comparative' or 'clientSchedule'
      cashFlowViewMode: 'comparative',

      // Export Section Selection (Checkbox options)
      exportSections: {
        financialPosition: true,
        changesInEquity: true,
        cashFlow: true,
        notes: true
      }
    };
  }

  async componentDidMount() {
    if (userid === undefined) {
      window.confirm('Your Session is Lost: Please login Back to resume');
      this.props.navigate(`/`);
      return;
    }

    try {
      if (typeof window !== 'undefined' && window.location && window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (e) {}

    this.setState({ loading: true });
    try {
      const fyResponse = await NewJournalService.getFinancialYears(currentTenant);
      const list = fyResponse.data || [];
      const def = { from: financialyearstart, to: financialyearend };
      const rawList = list.concat(def);

      const uniqueList = [];
      const seenFys = new Set();
      (rawList || []).forEach((item) => {
        if (!item || !item.from || !item.to) return;
        const fyInfo = this.getFinancialYearInfo(item.from, item.to);
        const key = fyInfo.label;
        if (!seenFys.has(key)) {
          seenFys.add(key);
          uniqueList.push(item);
        }
      });

      // Sort by descending startYear so latest is first
      uniqueList.sort((a, b) => {
        const infoA = this.getFinancialYearInfo(a.from, a.to);
        const infoB = this.getFinancialYearInfo(b.from, b.to);
        return infoB.startYear - infoA.startYear;
      });

      // Also fetch equity allocations
      this.fetchEquityAllocations();

      this.setState({ fylist: uniqueList }, () => {
        this.populateDefaultYear();
      });
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error === 'Unauthorized') {
        alert('Your Session is Lost: Please login Back to resume');
        this.props.navigate(`/`);
      } else {
        this.populateDefaultYear();
      }
    }
  }

  async fetchEquityAllocations() {
    try {
      const res = await axios.get(
        `${Auth_Proto}${Auth_Domain}${Auth_Port}/api/accounts/allocatlist/${currentTenant}`
      );
      const raw = res && res.data ? res.data : [];
      const data = Array.isArray(raw)
        ? raw
        : Array.isArray(raw.data)
        ? raw.data
        : Array.isArray(raw.result)
        ? raw.result
        : [];
      this.setState({ equityAllocations: data });
    } catch (err) {
      console.log('Error fetching equity allocation list:', err);
    }
  }

  populateDefaultYear() {
    const def = { from: financialyearstart, to: financialyearend };
    const defLabel = this.getYearLabel(financialyearstart, financialyearend);
    const currentFy =
      (this.state.fylist || []).find(
        (f) => f && this.getYearLabel(f.from, f.to) === defLabel
      ) ||
      (this.state.fylist && this.state.fylist.length > 0 ? this.state.fylist[0] : def);

    this.setState({ selectedFy: currentFy }, () => this.onFySelect(currentFy));
  }

  getFinancialYearInfo(from, to) {
    if (!from) return { startYear: 0, endYear: 0, label: '', isCalendarYear: true };
    const tz =
      clienttz ||
      (currentTenant === 'CLI100008' || ccountry === 'Qatar'
        ? 'Asia/Qatar'
        : ccountry === 'India'
        ? 'Asia/Kolkata'
        : 'UTC');

    let parts;
    try {
      const fDate = new Date(from);
      if (isNaN(fDate.getTime())) throw new Error();
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      parts = formatter.format(fDate); // YYYY-MM-DD
    } catch (e) {
      const s = String(from).substring(0, 4);
      const yr = parseInt(s, 10) || 0;
      return { startYear: yr, endYear: yr, label: `${yr}-${yr}`, isCalendarYear: true, startDateStr: `${yr}-01-01` };
    }

    const [yStr, mStr] = parts.split('-');
    const startYear = parseInt(yStr, 10);
    const startMonth = parseInt(mStr, 10);
    // When financial year starts in January (month 1), it is a calendar year: YYYY-YYYY
    const isCalendarYear = startMonth === 1;
    const endYear = isCalendarYear ? startYear : startYear + 1;
    const label = `${startYear}-${endYear}`;

    return {
      startYear,
      endYear,
      isCalendarYear,
      startMonth,
      label,
      startDateStr: parts
    };
  }

  getYearLabel(from, to) {
    if (!from) return '';
    return this.getFinancialYearInfo(from, to).label;
  }

  getEndDateLabel(to, from) {
    const fy = this.getFinancialYearInfo(from || to, to);
    if (!fy || !fy.startYear) return '';
    if (fy.isCalendarYear) {
      return `DECEMBER 31, ${fy.endYear}`;
    }
    if (fy.startMonth === 4) {
      return `MARCH 31, ${fy.endYear}`;
    }
    try {
      const tDate = new Date(to);
      const tz =
        clienttz ||
        (currentTenant === 'CLI100008' || ccountry === 'Qatar'
          ? 'Asia/Qatar'
          : ccountry === 'India'
          ? 'Asia/Kolkata'
          : 'UTC');
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        month: 'long',
        day: '2-digit',
        year: 'numeric'
      });
      return formatter.format(tDate).toUpperCase();
    } catch (e) {
      return `DECEMBER 31, ${fy.endYear}`;
    }
  }

  getStartDateLabel(from) {
    const fy = this.getFinancialYearInfo(from, null);
    if (!fy || !fy.startYear) return '';
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[(fy.startMonth || 1) - 1] || 'January';
    const day = (fy.startDateStr || '').split('-')[2] || '01';
    return `${monthName} ${day}, ${fy.startYear}`;
  }

  async fetchProfitDistribution(from, to) {
    const candidateYearIds = new Set();
    const yId = this.getYearLabel(from, to);
    if (yId) candidateYearIds.add(yId);

    if (this.state.fylist && this.state.fylist.length > 0) {
      const matched = this.state.fylist.find(
        (f) =>
          f &&
          f.from &&
          f.to &&
          this.getYearLabel(f.from, f.to) === yId
      );
      if (matched) {
        if (matched.id) candidateYearIds.add(String(matched.id));
      }
    }

    // Only allow 'current' if this requested period matches the tenant default current FY
    const isCurrentFy =
      financialyearstart &&
      financialyearend &&
      this.getYearLabel(from, to) === this.getYearLabel(financialyearstart, financialyearend);
    if (isCurrentFy) {
      candidateYearIds.add('current');
    }

    for (const id of candidateYearIds) {
      if (!id) continue;
      try {
        const res = await axios.get(
          `${Auth_Proto}${Auth_Domain}${Auth_Port}/api/accounts/record/${currentTenant}/${id}`
        );
        if (res.data && (res.data.id || res.data.netProfit !== undefined)) {
          return res.data;
        }
      } catch (err) {}
    }
    return null;
  }

  async fetchPartnerCurrent(from, to) {
    const candidateYearIds = new Set();
    const yId = this.getYearLabel(from, to);
    if (yId) candidateYearIds.add(yId);

    if (this.state.fylist && this.state.fylist.length > 0) {
      const matched = this.state.fylist.find(
        (f) =>
          f &&
          f.from &&
          f.to &&
          this.getYearLabel(f.from, f.to) === yId
      );
      if (matched) {
        if (matched.id) candidateYearIds.add(String(matched.id));
      }
    }

    // Only allow 'current' if this requested period matches the tenant default current FY
    const isCurrentFy =
      financialyearstart &&
      financialyearend &&
      this.getYearLabel(from, to) === this.getYearLabel(financialyearstart, financialyearend);
    if (isCurrentFy) {
      candidateYearIds.add('current');
    }

    for (const id of candidateYearIds) {
      if (!id) continue;
      try {
        const res = await axios.get(
          `${Auth_Proto}${Auth_Domain}${Auth_Port}/api/accounts/partnercurrent/${currentTenant}/${id}`
        );
        if (res.data && (res.data.id || res.data.totalAmount !== undefined)) {
          return res.data;
        }
      } catch (err) {}
    }
    return null;
  }

  async fetchPlValues(yearReq) {
    try {
      const plresp = await NewReportService.retrivePLLedgers(yearReq);
      const subaccdata = plresp && plresp.data && Array.isArray(plresp.data) ? plresp.data : [];
      let debTotal = 0;
      let creTotal = 0;
      subaccdata.forEach((e) => {
        const amt = Number(e.totamount) || 0;
        if (e.th === 'TH1') debTotal += amt;
        if (e.th === 'TH2') creTotal += amt;
      });
      const creditexcess = creTotal > debTotal ? creTotal - debTotal : 0;
      const debitexcess = debTotal > creTotal ? debTotal - creTotal : 0;
      return { debitexcess, creditexcess };
    } catch (e) {
      return { debitexcess: 0, creditexcess: 0 };
    }
  }

  onFySelect = async (value) => {
    if (!value) return;
    this.setState({ loading: true, loader1: true, selectedFy: value });

    const currentYearReq = {
      from: value.from,
      to: value.to,
      tenantid: currentTenant
    };

    // Derive prior financial years based on the exact period structure of the selected FY:
    // e.g. 2026-01-01..2026-12-31 -> prior is 2025-01-01..2025-12-31 (2025-2025)
    // e.g. 2025-04-01..2026-03-31 -> prior is 2024-04-01..2025-03-31 (2024-2025)
    const curFyInfo = this.getFinancialYearInfo(value.from, value.to);
    const targetPriorStartYear = curFyInfo.startYear - 1;
    const targetTwoYearsPriorStartYear = curFyInfo.startYear - 2;

    let priorFy = (this.state.fylist || []).find((f) => {
      if (!f) return false;
      const fInfo = this.getFinancialYearInfo(f.from, f.to);
      return fInfo.startYear === targetPriorStartYear;
    });

    if (!priorFy) {
      if (curFyInfo.isCalendarYear) {
        priorFy = {
          from: `${targetPriorStartYear}-01-01T00:00:01Z`,
          to: `${targetPriorStartYear}-12-31T23:59:59.999Z`
        };
      } else {
        priorFy = {
          from: `${targetPriorStartYear}-04-01T00:00:01Z`,
          to: `${targetPriorStartYear + 1}-03-31T23:59:59.999Z`
        };
      }
    }

    let twoYearsPriorFy = (this.state.fylist || []).find((f) => {
      if (!f) return false;
      const fInfo = this.getFinancialYearInfo(f.from, f.to);
      return fInfo.startYear === targetTwoYearsPriorStartYear;
    });

    if (!twoYearsPriorFy) {
      if (curFyInfo.isCalendarYear) {
        twoYearsPriorFy = {
          from: `${targetTwoYearsPriorStartYear}-01-01T00:00:01Z`,
          to: `${targetTwoYearsPriorStartYear}-12-31T23:59:59.999Z`
        };
      } else {
        twoYearsPriorFy = {
          from: `${targetTwoYearsPriorStartYear}-04-01T00:00:01Z`,
          to: `${targetTwoYearsPriorStartYear + 1}-03-31T23:59:59.999Z`
        };
      }
    }

    const priorYearReq = {
      from: priorFy.from,
      to: priorFy.to,
      tenantid: currentTenant
    };

    const twoYearsPriorYearReq = {
      from: twoYearsPriorFy.from,
      to: twoYearsPriorFy.to,
      tenantid: currentTenant
    };

    this.setState({
      fyfrom: value.from,
      fyto: value.to,
      priorFy: priorFy,
      priorFyfrom: priorFy.from,
      priorFyto: priorFy.to,
      twoYearsPriorFy: twoYearsPriorFy
    });

    try {
      const [
        currAsstRes,
        currLiabRes,
        currPlVal,
        currDist,
        currPartnerCurrent,
        priorAsstRes,
        priorLiabRes,
        priorPlVal,
        priorDist,
        priorPartnerCurrent,
        twoYrsAsstRes,
        twoYrsLiabRes,
        twoYrsPlVal,
        twoYrsDist,
        twoYrsPartnerCurrent
      ] = await Promise.all([
        NewJournalService.getAssets(currentYearReq).catch(() => ({ data: [] })),
        NewJournalService.getLiabilities(currentYearReq).catch(() => ({ data: [] })),
        this.fetchPlValues(currentYearReq),
        this.fetchProfitDistribution(value.from, value.to),
        this.fetchPartnerCurrent(value.from, value.to),

        NewJournalService.getAssets(priorYearReq).catch(() => ({ data: [] })),
        NewJournalService.getLiabilities(priorYearReq).catch(() => ({ data: [] })),
        this.fetchPlValues(priorYearReq),
        this.fetchProfitDistribution(priorFy.from, priorFy.to),
        this.fetchPartnerCurrent(priorFy.from, priorFy.to),

        NewJournalService.getAssets(twoYearsPriorYearReq).catch(() => ({ data: [] })),
        NewJournalService.getLiabilities(twoYearsPriorYearReq).catch(() => ({ data: [] })),
        this.fetchPlValues(twoYearsPriorYearReq),
        this.fetchProfitDistribution(twoYearsPriorFy.from, twoYearsPriorFy.to),
        this.fetchPartnerCurrent(twoYearsPriorFy.from, twoYearsPriorFy.to)
      ]);

      if (currPlVal.debitexcess > 0) {
        Credentials.setPlprofit(currPlVal.debitexcess);
      } else if (currPlVal.creditexcess > 0) {
        Credentials.setPlloss(currPlVal.creditexcess);
      }

      const currNetProfit =
        (currPlVal.debitexcess || 0) > 0
          ? Number(currPlVal.debitexcess)
          : -(Number(currPlVal.creditexcess) || 0);

      const priorNetProfit =
        (priorPlVal.debitexcess || 0) > 0
          ? Number(priorPlVal.debitexcess)
          : -(Number(priorPlVal.creditexcess) || 0);

      const twoYearsPriorNetProfit =
        (twoYrsPlVal.debitexcess || 0) > 0
          ? Number(twoYrsPlVal.debitexcess)
          : -(Number(twoYrsPlVal.creditexcess) || 0);

      this.setState({
        currAssets: currAsstRes.data || [],
        currLiabilities: currLiabRes.data || [],
        currPl: { ...currPlVal, netProfit: currNetProfit },
        currProfitDist: currDist,
        currPartnerCurrent: currPartnerCurrent,

        priorAssets: priorAsstRes.data || [],
        priorLiabilities: priorLiabRes.data || [],
        priorPl: { ...priorPlVal, netProfit: priorNetProfit },
        priorProfitDist: priorDist,
        priorPartnerCurrent: priorPartnerCurrent,

        twoYearsPriorAssets: twoYrsAsstRes.data || [],
        twoYearsPriorLiabilities: twoYrsLiabRes.data || [],
        twoYearsPriorPl: { ...twoYrsPlVal, netProfit: twoYearsPriorNetProfit },
        twoYearsPriorProfitDist: twoYrsDist,
        twoYearsPriorPartnerCurrent: twoYrsPartnerCurrent,

        loading: false,
        loader1: false
      });

      try {
        const fyObj = { from: value.from, to: value.to };
        const fyParam = encodeURIComponent(JSON.stringify(fyObj));
        if (this.props && this.props.navigate) {
          const pathname = this.props.location
            ? this.props.location.pathname
            : window.location.pathname;
          this.props.navigate(`${pathname}?fy=${fyParam}`, { replace: true });
        }
      } catch (e) {}
    } catch (err) {
      console.error('Error fetching balance sheet statement data:', err);
      this.setState({ loading: false, loader1: false });
    }
  };

  /* -------------------------------------------------------------
   * Classification & Aggregation Helpers for Template Buckets
   * ------------------------------------------------------------- */
  classifyFinancialData(assetsList, liabilitiesList, plData, profitDist, partnerCurrent) {
    const str = (s) => (s ? String(s).toLowerCase() : '');

    const propertyAndEquipment = [];
    const cashAndBanks = [];
    const accountReceivables = [];
    const inventory = [];
    const dueFromRelated = [];
    const otherDebitBalances = [];
    const establishmentExpenses = [];
    const depreciationLedgers = [];

    (assetsList || []).forEach((item) => {
      const sh = str(item.shname);
      const name = str(item.name || item.ledname);

      if (
        sh.includes('accumulated deprec') ||
        sh.includes('accum deprec') ||
        sh.includes('depreciation') ||
        name.includes('accumulated deprec') ||
        name.includes('accum deprec')
      ) {
        depreciationLedgers.push(item);
      }

      if (
        sh.includes('establishment') ||
        sh.includes('pre-operat') ||
        sh.includes('pre operat') ||
        sh.includes('preliminary') ||
        name.includes('establishment') ||
        name.includes('pre-operat') ||
        name.includes('pre operat') ||
        name.includes('preliminary')
      ) {
        establishmentExpenses.push(item);
      }

      if (
        sh.includes('fixed') ||
        sh.includes('property') ||
        sh.includes('equipment') ||
        sh.includes('machinery') ||
        sh.includes('vehicle') ||
        sh.includes('furniture') ||
        sh.includes('computer') ||
        (sh.includes('asset') && (sh.includes('non') || sh.includes('fixed'))) ||
        item.th === 'TH4'
      ) {
        propertyAndEquipment.push(item);
      } else if (
        sh.includes('cash') ||
        sh.includes('bank') ||
        name.includes('cash in hand') ||
        name.includes('petty cash') ||
        name.includes('bank')
      ) {
        cashAndBanks.push(item);
      } else if (
        sh.includes('due from related') ||
        sh.includes('related part') ||
        name.includes('due from related') ||
        item.partyledtype === 2
      ) {
        dueFromRelated.push(item);
      } else if (
        sh.includes('inventory') ||
        sh.includes('stock') ||
        name.includes('closing stock') ||
        name.includes('raw material')
      ) {
        inventory.push(item);
      } else if (
        (item.id && (String(item.id).startsWith('PLED') || String(item.id).startsWith('PL'))) ||
        item.partyledtype === 0 ||
        item.rule === 0 ||
        item.behaviour === 7 ||
        item.type === 0 ||
        sh.includes('receivable') ||
        sh.includes('debtor') ||
        sh.includes('trade debtor') ||
        sh.includes('sundry debtor') ||
        sh.includes('customer') ||
        sh.includes('client') ||
        name.includes('receivable') ||
        name.includes('debtor') ||
        name.includes('customer')
      ) {
        accountReceivables.push(item);
      } else {
        otherDebitBalances.push(item);
      }
    });

    const capitalLedgers = [];
    const longTermLoans = [];
    const accountsPayable = [];
    const dueToRelated = [];
    const otherCreditBalances = [];
    const endOfServiceLiabilities = [];

    (liabilitiesList || []).forEach((item) => {
      const sh = str(item.shname);
      const name = str(item.name || item.ledname);

      if (
        sh.includes('end of service') ||
        sh.includes('eos') ||
        sh.includes('indemnity') ||
        sh.includes('gratuity') ||
        name.includes('end of service') ||
        name.includes('eos') ||
        name.includes('indemnity') ||
        name.includes('gratuity')
      ) {
        endOfServiceLiabilities.push(item);
      }

      if (
        sh.includes('capital') ||
        sh.includes('share') ||
        sh.includes('equity') ||
        name.includes('capital') ||
        name.includes('share capital')
      ) {
        capitalLedgers.push(item);
      } else if (
        sh.includes('long term') ||
        sh.includes('term loan') ||
        sh.includes('borrowing') ||
        sh.includes('mortgage') ||
        sh.includes('secured loan')
      ) {
        longTermLoans.push(item);
      } else if (
        sh.includes('due to related') ||
        sh.includes('related part') ||
        name.includes('due to related') ||
        name.includes('director loan') ||
        item.partyledtype === 2
      ) {
        dueToRelated.push(item);
      } else if (
        (item.id && (String(item.id).startsWith('PLED') || String(item.id).startsWith('PL'))) ||
        item.partyledtype === 0 ||
        item.rule === 0 ||
        item.behaviour === 6 ||
        item.type === 1 ||
        sh.includes('payable') ||
        sh.includes('creditor') ||
        sh.includes('trade creditor') ||
        sh.includes('sundry creditor') ||
        sh.includes('vendor') ||
        sh.includes('supplier') ||
        name.includes('payable') ||
        name.includes('creditor') ||
        name.includes('supplier')
      ) {
        accountsPayable.push(item);
      } else {
        otherCreditBalances.push(item);
      }
    });

    const sumAmounts = (arr) =>
      arr.reduce((tot, item) => tot + Math.abs(Number(item.amount) || 0), 0);

    const totalPropertyEquipment = sumAmounts(propertyAndEquipment);
    const totalNonCurrentAssets = totalPropertyEquipment;

    const totalCashAndBanks = sumAmounts(cashAndBanks);
    const totalAccountReceivables = sumAmounts(accountReceivables);
    const totalInventory = sumAmounts(inventory);
    const totalDueFromRelated = sumAmounts(dueFromRelated);
    const totalOtherDebitBalances = sumAmounts(otherDebitBalances);

    const totalEstablishmentExpenses = sumAmounts(establishmentExpenses);
    const totalEndOfService = sumAmounts(endOfServiceLiabilities);
    const totalDepreciation = sumAmounts(depreciationLedgers);

    const totalCurrentAssets =
      totalCashAndBanks +
      totalAccountReceivables +
      totalInventory +
      totalDueFromRelated +
      totalOtherDebitBalances;

    const totalAssets = totalNonCurrentAssets + totalCurrentAssets;

    const capitalTotal = sumAmounts(capitalLedgers);
    let statutoryReserve = 0;
    let retainedEarnings = 0;
    let ownerEquityAmount = 0;
    let partnerAllocations = [];

    let netProfit = 0;
    if (profitDist && profitDist.netProfit !== undefined && profitDist.netProfit !== null) {
      netProfit = Number(profitDist.netProfit);
    } else if (plData) {
      if (plData.netProfit !== undefined && plData.netProfit !== null) {
        netProfit = Number(plData.netProfit);
      } else if (Number(plData.debitexcess) > 0) {
        netProfit = Number(plData.debitexcess);
      } else if (Number(plData.creditexcess) > 0) {
        netProfit = -Number(plData.creditexcess);
      }
    }

    if (partnerCurrent) {
      ownerEquityAmount = Number(partnerCurrent.totalAmount || 0);
      try {
        const raw = typeof partnerCurrent.partnerDetails === 'string'
          ? JSON.parse(partnerCurrent.partnerDetails)
          : partnerCurrent.partnerDetails || [];
        partnerAllocations = raw.map(p => ({
          partnerId: p.partnerId,
          partnerName: p.partnerName,
          sharePercentage: p.sharePercentage,
          allocatedAmount: p.closingAmount !== undefined ? p.closingAmount : p.allocatedAmount
        }));
      } catch (e) {
        partnerAllocations = [];
      }

      if (profitDist) {
        if (Number(profitDist.netProfit) < 0) {
          statutoryReserve = 0;
          retainedEarnings =
            profitDist.retainedEarningsAmount !== null && Number(profitDist.retainedEarningsAmount) < 0
              ? Number(profitDist.retainedEarningsAmount)
              : -Math.abs(Number(profitDist.netProfit));
        } else {
          statutoryReserve = Number(profitDist.statutoryReserveAmount || 0);
          retainedEarnings = Number(profitDist.retainedEarningsAmount || 0);
        }
      } else {
        if (plData && plData.debitexcess > 0) retainedEarnings = Number(plData.debitexcess);
        else if (plData && plData.creditexcess > 0) retainedEarnings = -Number(plData.creditexcess);
      }
    } else if (profitDist) {
      if (Number(profitDist.netProfit) < 0) {
        statutoryReserve = 0;
        retainedEarnings =
          profitDist.retainedEarningsAmount !== null && Number(profitDist.retainedEarningsAmount) < 0
            ? Number(profitDist.retainedEarningsAmount)
            : -Math.abs(Number(profitDist.netProfit));
        ownerEquityAmount = Number(profitDist.totalDistributedAmount || 0);
      } else {
        statutoryReserve = Number(profitDist.statutoryReserveAmount || 0);
        retainedEarnings = Number(profitDist.retainedEarningsAmount || 0);
        ownerEquityAmount = Number(profitDist.totalDistributedAmount || 0);
      }

      try {
        partnerAllocations =
          typeof profitDist.partnerAllocations === 'string'
            ? JSON.parse(profitDist.partnerAllocations)
            : profitDist.partnerAllocations || [];
      } catch (e) {
        partnerAllocations = [];
      }
    } else {
      if (plData && plData.debitexcess > 0) retainedEarnings = Number(plData.debitexcess);
      else if (plData && plData.creditexcess > 0) retainedEarnings = -Number(plData.creditexcess);
    }

    const totalPartnersEquity =
      capitalTotal + statutoryReserve + ownerEquityAmount + retainedEarnings;

    const totalLongTermLoans = sumAmounts(longTermLoans);
    const totalNonCurrentLiabilities = totalLongTermLoans;

    const totalAccountsPayable = sumAmounts(accountsPayable);
    const totalDueToRelated = sumAmounts(dueToRelated);
    const totalOtherCreditBalances = sumAmounts(otherCreditBalances);

    const totalCurrentLiabilities =
      totalAccountsPayable + totalDueToRelated + totalOtherCreditBalances;

    const totalPartnersEquityAndLiabilities =
      totalPartnersEquity + totalNonCurrentLiabilities + totalCurrentLiabilities;

    return {
      propertyAndEquipment,
      cashAndBanks,
      accountReceivables,
      inventory,
      dueFromRelated,
      otherDebitBalances,
      establishmentExpenses,
      depreciationLedgers,
      capitalLedgers,
      longTermLoans,
      accountsPayable,
      dueToRelated,
      otherCreditBalances,
      endOfServiceLiabilities,
      partnerAllocations,

      totalPropertyEquipment,
      totalNonCurrentAssets,
      totalCashAndBanks,
      totalAccountReceivables,
      totalInventory,
      totalDueFromRelated,
      totalOtherDebitBalances,
      totalEstablishmentExpenses,
      totalEndOfService,
      totalDepreciation,
      totalCurrentAssets,
      totalAssets,

      netProfit,
      capitalTotal,
      statutoryReserve,
      ownerEquityAmount,
      retainedEarnings,
      totalPartnersEquity,

      totalLongTermLoans,
      totalNonCurrentLiabilities,

      totalAccountsPayable,
      totalDueToRelated,
      totalOtherCreditBalances,
      totalCurrentLiabilities,
      totalPartnersEquityAndLiabilities
    };
  }

  renderAccountingValue(val, isBold = false) {
    if (val === null || val === undefined || val === '') return '-';
    const num = Number(val);
    if (isNaN(num) || num === 0) return '-';

    const isNegative = num < 0;
    const absVal = Math.abs(num);

    return (
      <span className={isNegative ? 'text-danger' : isBold ? 'font-weight-bold' : ''}>
        {isNegative ? '(' : ''}
        <NumericFormat
          displayType={'text'}
          decimalScale={2}
          fixedDecimalScale={true}
          thousandSeparator={true}
          thousandsGroupStyle={thousandsdisplay || 'lakh'}
          value={absVal}
        />
        {isNegative ? ')' : ''}
      </span>
    );
  }

  formatPercentage(pct) {
    if (pct === null || pct === undefined || isNaN(Number(pct))) return '-';
    const num = Number(pct);
    const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    return `${formatted}%`;
  }

  /* -------------------------------------------------------------
   * Helper to Resolve Share Capital Partners, Actual Percentages & Amounts
   * ------------------------------------------------------------- */
  getShareCapitalRows(data, equityAllocations = []) {
    const capitalTotal = Number(data.capitalTotal) || 0;

    // Priority 1: Check active equity allocations (EquityEntry / Share Capital setup)
    if (Array.isArray(equityAllocations) && equityAllocations.length > 0) {
      const activeEntries = equityAllocations.filter(
        (e) => !e.status || String(e.status).toLowerCase() === 'active'
      );

      if (activeEntries.length > 0) {
        const groups = new Map();
        for (const it of activeEntries) {
          const pid = it.partnerId || it.partnerLabel || it.id || it.name;
          const upd = it.updatedAt || it.createdAt || null;
          if (!pid) continue;
          const key = String(pid);
          if (!groups.has(key)) {
            groups.set(key, { record: it, updatedAt: upd });
          } else {
            const ex = groups.get(key);
            if (upd && ex.updatedAt && new Date(upd) > new Date(ex.updatedAt)) {
              groups.set(key, { record: it, updatedAt: upd });
            }
          }
        }

        const entries = Array.from(groups.values()).map((g) => g.record);
        if (entries.length > 0) {
          const sumAmts = entries.reduce((s, it) => s + (Number(it.amount) || 0), 0);

          return entries.map((it) => {
            const name =
              it.partnerLabel ||
              it.partnerName ||
              it.name ||
              `Partner (${it.partnerId || it.id})`;
            let pct = Number(it.partnerPercentage || it.percentage || 0);
            let amt = Number(it.amount || 0);

            if (pct === 0 && sumAmts > 0 && amt > 0) {
              pct = (amt / sumAmts) * 100;
            } else if (pct === 0 && capitalTotal > 0 && amt > 0) {
              pct = (amt / capitalTotal) * 100;
            } else if (pct === 0 && entries.length > 0) {
              pct = 100 / entries.length;
            }

            if (amt === 0 && capitalTotal > 0 && pct > 0) {
              amt = (capitalTotal * pct) / 100;
            }

            return {
              partnerName: name,
              percentage: pct,
              amount: amt,
              isLink: Boolean(it.partnerId || it.id),
              id: it.partnerId || it.id
            };
          });
        }
      }
    }

    // Priority 2: Check partner allocations from Profit Distribution record
    if (data.partnerAllocations && data.partnerAllocations.length > 0) {
      return data.partnerAllocations.map((p) => {
        const name = p.partnerName || `Partner (${p.partnerId || ''})`;
        let pct = Number(p.sharePercentage || 0);
        if (pct === 0 && data.partnerAllocations.length > 0) {
          pct = 100 / data.partnerAllocations.length;
        }

        let amt = 0;
        if (capitalTotal > 0 && pct > 0) {
          amt = (capitalTotal * pct) / 100;
        } else if (p.allocatedAmount) {
          amt = Number(p.allocatedAmount);
        }

        return {
          partnerName: name,
          percentage: pct,
          amount: amt,
          isLink: Boolean(p.partnerId || p.id),
          id: p.partnerId || p.id
        };
      });
    }

    // Priority 3: Check capital ledgers from Liabilities / Trial Balance
    if (data.capitalLedgers && data.capitalLedgers.length > 0) {
      return data.capitalLedgers.map((l) => {
        const name = l.name || l.ledname || 'Capital Account';
        const amt = Math.abs(Number(l.amount) || 0);
        const pct =
          capitalTotal > 0 ? (amt / capitalTotal) * 100 : 100 / data.capitalLedgers.length;

        return {
          partnerName: name,
          percentage: pct,
          amount: amt,
          isLink: true,
          id: l.id
        };
      });
    }

    // Fallback: Single Capital Account
    return [
      {
        partnerName: 'Capital Account',
        percentage: 100,
        amount: capitalTotal,
        isLink: false
      }
    ];
  }

  computeCashFlowStatement = (currData, priorData) => {
    if (!currData) {
      return {
        netProfitLoss: 0,
        depreciation: null,
        financeChargesAdj: null,
        endOfServiceAdj: null,
        cashFlowBeforeWC: 0,
        accountsReceivableMovement: null,
        inventoryMovement: null,
        otherDebitBalancesMovement: null,
        dueFromRelatedMovement: null,
        accountsPayableMovement: null,
        otherCreditBalancesMovement: null,
        dueToRelatedMovement: null,
        establishmentExpensesMovement: null,
        netCashOperating: 0,
        acquisitionFixedAssets: null,
        netCashInvesting: null,
        capitalContributions: null,
        partnerCurrentChanges: null,
        loansMovements: null,
        financeChargesPaid: null,
        netCashFinancing: null,
        netCashIncrease: 0,
        cashBeginning: 0,
        cashEndCF: 0,
        cashEndFP: 0,
        variance: 0
      };
    }

    const netProfit = Number(currData.netProfit || 0);

    // Depreciation adjustment
    let depreciation = null;
    if (currData.totalDepreciation > 0 || (priorData && priorData.totalDepreciation > 0)) {
      const depDiff = currData.totalDepreciation - (priorData ? priorData.totalDepreciation : 0);
      if (depDiff > 0) depreciation = depDiff;
    }

    // End of service adjustment
    const currEos = Number(currData.totalEndOfService || 0);
    const priorEos = priorData ? Number(priorData.totalEndOfService || 0) : 0;
    const endOfServiceAdj = priorData && currEos - priorEos !== 0 ? currEos - priorEos : null;

    const financeChargesAdj = null;

    const cashFlowBeforeWC =
      netProfit + (depreciation || 0) + (financeChargesAdj || 0) + (endOfServiceAdj || 0);

    // Working Capital Movements:
    // Asset increases -> negative cash impact; Asset decreases -> positive cash impact
    const priorAr = priorData ? Number(priorData.totalAccountReceivables || 0) : 0;
    const currAr = Number(currData.totalAccountReceivables || 0);
    const arDiff = currAr - priorAr;
    const accountsReceivableMovement = priorData && arDiff !== 0 ? -arDiff : null;

    const priorInv = priorData ? Number(priorData.totalInventory || 0) : 0;
    const currInv = Number(currData.totalInventory || 0);
    const invDiff = currInv - priorInv;
    const inventoryMovement = priorData && invDiff !== 0 ? -invDiff : null;

    const priorOdb = priorData ? Number(priorData.totalOtherDebitBalances || 0) : 0;
    const currOdb = Number(currData.totalOtherDebitBalances || 0);
    const odbDiff = currOdb - priorOdb;
    const otherDebitBalancesMovement = priorData && odbDiff !== 0 ? -odbDiff : null;

    const priorDfr = priorData ? Number(priorData.totalDueFromRelated || 0) : 0;
    const currDfr = Number(currData.totalDueFromRelated || 0);
    const dfrDiff = currDfr - priorDfr;
    const dueFromRelatedMovement = priorData && dfrDiff !== 0 ? -dfrDiff : null;

    // Liability increases -> positive cash impact; Liability decreases -> negative cash impact
    const priorAp = priorData ? Number(priorData.totalAccountsPayable || 0) : 0;
    const currAp = Number(currData.totalAccountsPayable || 0);
    const apDiff = currAp - priorAp;
    const accountsPayableMovement = priorData && apDiff !== 0 ? apDiff : null;

    const priorOcb = priorData ? Number(priorData.totalOtherCreditBalances || 0) : 0;
    const currOcb = Number(currData.totalOtherCreditBalances || 0);
    const ocbDiff = currOcb - priorOcb;
    const otherCreditBalancesMovement = priorData && ocbDiff !== 0 ? ocbDiff : null;

    const priorDtr = priorData ? Number(priorData.totalDueToRelated || 0) : 0;
    const currDtr = Number(currData.totalDueToRelated || 0);
    const dtrDiff = currDtr - priorDtr;
    const dueToRelatedMovement = priorData && dtrDiff !== 0 ? dtrDiff : null;

    const priorEst = priorData ? Number(priorData.totalEstablishmentExpenses || 0) : 0;
    const currEst = Number(currData.totalEstablishmentExpenses || 0);
    const estDiff = currEst - priorEst;
    const establishmentExpensesMovement = priorData && estDiff !== 0 ? -estDiff : null;

    const netCashOperating =
      cashFlowBeforeWC +
      (accountsReceivableMovement || 0) +
      (inventoryMovement || 0) +
      (otherDebitBalancesMovement || 0) +
      (dueFromRelatedMovement || 0) +
      (accountsPayableMovement || 0) +
      (otherCreditBalancesMovement || 0) +
      (dueToRelatedMovement || 0) +
      (establishmentExpensesMovement || 0);

    // Investing Activities
    const priorFa = priorData ? Number(priorData.totalPropertyEquipment || 0) : 0;
    const currFa = Number(currData.totalPropertyEquipment || 0);
    const faDiff = currFa - priorFa;
    const acquisitionFixedAssets =
      priorData && (faDiff !== 0 || (depreciation && depreciation > 0))
        ? -(faDiff + (depreciation || 0))
        : null;
    const netCashInvesting = acquisitionFixedAssets !== null ? acquisitionFixedAssets : null;

    // Financing Activities
    const priorCap = priorData ? Number(priorData.capitalTotal || 0) : 0;
    const currCap = Number(currData.capitalTotal || 0);
    const capDiff = currCap - priorCap;
    const capitalContributions = priorData && capDiff !== 0 ? capDiff : null;

    const priorOe = priorData ? Number(priorData.ownerEquityAmount || 0) : 0;
    const currOe = Number(currData.ownerEquityAmount || 0);
    const oeDiff = currOe - priorOe;
    const partnerCurrentChanges = priorData && oeDiff !== 0 ? oeDiff : null;

    const priorLoans = priorData ? Number(priorData.totalLongTermLoans || 0) : 0;
    const currLoans = Number(currData.totalLongTermLoans || 0);
    const loanDiff = currLoans - priorLoans;
    const loansMovements = priorData && loanDiff !== 0 ? loanDiff : null;

    const financeChargesPaid = null;

    const hasFinancing =
      capitalContributions !== null ||
      partnerCurrentChanges !== null ||
      loansMovements !== null ||
      financeChargesPaid !== null;

    const netCashFinancing = hasFinancing
      ? (capitalContributions || 0) +
        (partnerCurrentChanges || 0) +
        (loansMovements || 0) +
        (financeChargesPaid || 0)
      : null;

    // Cash Reconciliation
    const netCashIncrease =
      netCashOperating + (netCashInvesting || 0) + (netCashFinancing || 0);
    const cashBeginning = priorData ? Number(priorData.totalCashAndBanks || 0) : 0;
    const cashEndCF = cashBeginning + netCashIncrease;
    const cashEndFP = Number(currData.totalCashAndBanks || 0);
    const variance = cashEndCF - cashEndFP;

    return {
      netProfitLoss: netProfit,
      depreciation,
      financeChargesAdj,
      endOfServiceAdj,
      cashFlowBeforeWC,
      accountsReceivableMovement,
      inventoryMovement,
      otherDebitBalancesMovement,
      dueFromRelatedMovement,
      accountsPayableMovement,
      otherCreditBalancesMovement,
      dueToRelatedMovement,
      establishmentExpensesMovement,
      netCashOperating,
      acquisitionFixedAssets,
      netCashInvesting,
      capitalContributions,
      partnerCurrentChanges,
      loansMovements,
      financeChargesPaid,
      netCashFinancing,
      netCashIncrease,
      cashBeginning,
      cashEndCF,
      cashEndFP,
      variance
    };
  };

  navigateToNote = (noteNumber) => {
    this.setState({ activeTab: 3, activeNoteAnchor: `note-${noteNumber}` }, () => {
      setTimeout(() => {
        const el = document.getElementById(`note-${noteNumber}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    });
  };

  handleSectionToggle = (key) => {
    this.setState((prev) => ({
      exportSections: {
        ...prev.exportSections,
        [key]: !prev.exportSections[key]
      }
    }));
  };

  handleSelectAllSections = (selectAll) => {
    this.setState({
      exportSections: {
        financialPosition: selectAll,
        changesInEquity: selectAll,
        cashFlow: selectAll,
        notes: selectAll
      }
    });
  };

  /* -------------------------------------------------------------
   * Render Tab 1: Statement of Financial Position (Page 1 Template)
   * ------------------------------------------------------------- */
  renderFinancialPositionStatement(currData, priorData) {
    const currYearLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
    const priorYearLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);
    const curUnit = currency || 'QR.';

    const renderNoteBadge = (num) => (
      <span
        onClick={() => this.navigateToNote(num)}
        style={{
          cursor: 'pointer',
          padding: '2px 8px',
          borderRadius: '4px',
          backgroundColor: '#f0f4f8',
          color: '#1a56db',
          fontWeight: 'bold',
          fontSize: '11px',
          border: '1px solid #d0e0fc'
        }}
        title={`View Note ${num} Breakdown`}
      >
        {num}
      </span>
    );

    return (
      <div className="p-3 bg-white" style={{ maxWidth: '960px', margin: '0 auto', fontFamily: 'serif' }}>
        <div className="text-center mb-4">
          <h4 className="font-weight-bold mb-1" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
            {clientname || 'COMPANY NAME'}
          </h4>
          <div className="text-muted small mb-1">With Limited Liabilities</div>
          <div className="text-muted small font-weight-bold mb-2">
            {ccity ? `${ccity} - ` : ''}
            {ccountry || ''}
          </div>
          <h5 className="font-weight-bold text-dark mb-1" style={{ textDecoration: 'underline' }}>
            FINANCIAL POSITION AS OF {this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}
          </h5>
        </div>

        <Table className="table table-sm table-borderless" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow style={{ borderBottom: '2px solid #000' }}>
              <TableCell className="font-weight-bold text-left p-2" style={{ width: '50%' }}>
                Particulars
              </TableCell>
              <TableCell className="font-weight-bold text-center p-2" style={{ width: '10%' }}>
                Note
              </TableCell>
              <TableCell className="font-weight-bold text-right p-2" style={{ width: '20%' }}>
                <div>{currYearLabel || 'Current'}</div>
                <div className="small font-weight-normal">{curUnit}</div>
              </TableCell>
              <TableCell className="font-weight-bold text-right p-2" style={{ width: '20%' }}>
                <div>{priorYearLabel || 'Prior'}</div>
                <div className="small font-weight-normal">{curUnit}</div>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* ASSETS SECTION */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pt-3 pb-1" style={{ fontSize: '15px' }}>
                Assets:
              </TableCell>
            </TableRow>

            {/* Non-current Assets */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pl-3 pt-1 pb-1" style={{ fontSize: '14px' }}>
                Non-current Assets:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Property & Equipment</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(3)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalPropertyEquipment)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalPropertyEquipment)}
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
              <TableCell className="text-left pl-4 font-weight-bold py-1">Total Non-current Assets</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(currData.totalNonCurrentAssets, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(priorData.totalNonCurrentAssets, true)}
              </TableCell>
            </TableRow>

            {/* Current Assets */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pl-3 pt-3 pb-1" style={{ fontSize: '14px' }}>
                Current Assets:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Cash on hand and at banks</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(4)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalCashAndBanks)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalCashAndBanks)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Account Receivables</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(5)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalAccountReceivables)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalAccountReceivables)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Inventory</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(6)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalInventory)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalInventory)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Other Debit Balances</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(7)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalOtherDebitBalances)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalOtherDebitBalances)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Due from related parties</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalDueFromRelated)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalDueFromRelated)}
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
              <TableCell className="text-left pl-4 font-weight-bold py-1">Total Current Assets</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(currData.totalCurrentAssets, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(priorData.totalCurrentAssets, true)}
              </TableCell>
            </TableRow>

            {/* Total Assets (Double Underline) */}
            <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
              <TableCell className="text-left pl-3 font-weight-bold py-2" style={{ fontSize: '14px' }}>
                Total Assets
              </TableCell>
              <TableCell className="text-center py-2" />
              <TableCell className="text-right font-weight-bold py-2" style={{ fontSize: '14px' }}>
                {this.renderAccountingValue(currData.totalAssets, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-2" style={{ fontSize: '14px' }}>
                {this.renderAccountingValue(priorData.totalAssets, true)}
              </TableCell>
            </TableRow>

            {/* OWNER EQUITY AND LIABILITIES SECTION */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pt-4 pb-1" style={{ fontSize: '15px' }}>
                Owner Equity and Liabilities
              </TableCell>
            </TableRow>

            {/* Owner Equity */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pl-3 pt-1 pb-1" style={{ fontSize: '14px' }}>
                Owner Equity:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">capital</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(10)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.capitalTotal)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.capitalTotal)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Statutory reserve</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(11)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.statutoryReserve)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.statutoryReserve)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">
                Owner Equity
                <Link
                  to="/partnercurrent"
                  className="ml-2 badge badge-info text-white"
                  style={{ textDecoration: 'none', fontSize: '10px', padding: '2px 6px', verticalAlign: 'middle' }}
                  title="Manage Partner Current / Owner Equity"
                >
                  <i className="fas fa-edit mr-1"></i> Edit
                </Link>
              </TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.ownerEquityAmount)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.ownerEquityAmount)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Retained earnings</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(12)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.retainedEarnings)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.retainedEarnings)}
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
              <TableCell className="text-left pl-4 font-weight-bold py-1">Total Partners' Equity</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(currData.totalPartnersEquity, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(priorData.totalPartnersEquity, true)}
              </TableCell>
            </TableRow>

            {/* NON-Current Liabilities */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pl-3 pt-3 pb-1" style={{ fontSize: '14px' }}>
                NON-Current liabilities:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Long term loans</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalLongTermLoans)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalLongTermLoans)}
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
              <TableCell className="text-left pl-4 font-weight-bold py-1">Total NON-Current Liabilities</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(currData.totalNonCurrentLiabilities, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(priorData.totalNonCurrentLiabilities, true)}
              </TableCell>
            </TableRow>

            {/* Current Liabilities */}
            <TableRow>
              <TableCell colSpan={4} className="font-weight-bold text-left pl-3 pt-3 pb-1" style={{ fontSize: '14px' }}>
                Current liabilities:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Accounts Payable</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(8)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalAccountsPayable)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalAccountsPayable)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Other Credit Balances</TableCell>
              <TableCell className="text-center py-1">{renderNoteBadge(9)}</TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalOtherCreditBalances)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalOtherCreditBalances)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-left pl-4 py-1">Due to related parties</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(currData.totalDueToRelated)}
              </TableCell>
              <TableCell className="text-right py-1">
                {this.renderAccountingValue(priorData.totalDueToRelated)}
              </TableCell>
            </TableRow>
            <TableRow style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
              <TableCell className="text-left pl-4 font-weight-bold py-1">Total Current Liabilities</TableCell>
              <TableCell className="text-center py-1" />
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(currData.totalCurrentLiabilities, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-1">
                {this.renderAccountingValue(priorData.totalCurrentLiabilities, true)}
              </TableCell>
            </TableRow>

            {/* Total Partners' Equity and Liabilities (Double Underline) */}
            <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
              <TableCell className="text-left pl-3 font-weight-bold py-2" style={{ fontSize: '14px' }}>
                Total Partners' Equity and Liabilities
              </TableCell>
              <TableCell className="text-center py-2" />
              <TableCell className="text-right font-weight-bold py-2" style={{ fontSize: '14px' }}>
                {this.renderAccountingValue(currData.totalPartnersEquityAndLiabilities, true)}
              </TableCell>
              <TableCell className="text-right font-weight-bold py-2" style={{ fontSize: '14px' }}>
                {this.renderAccountingValue(priorData.totalPartnersEquityAndLiabilities, true)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="text-center text-muted mt-4 small font-italic">
          The accompanying notes form an integral part of these financial statements.
          <div className="mt-1">-1-</div>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------
   * Render Tab 2: Statement of Changes in Partners' Equity (Page 3 Template)
   * ------------------------------------------------------------- */
  renderChangesInEquity(currData, priorData) {
    const curUnit = currency || 'QR.';
    const currLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
    const priorLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);

    return (
      <div className="p-3 bg-white" style={{ maxWidth: '960px', margin: '0 auto', fontFamily: 'serif' }}>
        <div className="text-center mb-4">
          <h4 className="font-weight-bold mb-1" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
            {clientname || 'COMPANY NAME'}
          </h4>
          <div className="text-muted small mb-1">With Limited Liabilities</div>
          <div className="text-muted small font-weight-bold mb-2">
            {ccity ? `${ccity} - ` : ''}
            {ccountry || ''}
          </div>
          <h5 className="font-weight-bold text-dark mb-1" style={{ textDecoration: 'underline' }}>
            STATEMENT OF CHANGES IN PARTNERS' EQUITY FOR THE YEAR ENDED {this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}
          </h5>
        </div>

        <Table className="table table-sm table-bordered text-center" style={{ width: '100%' }}>
          <TableHead className="thead-light">
            <TableRow>
              <TableCell className="font-weight-bold text-left" style={{ width: '30%' }}>
                Particulars
              </TableCell>
              <TableCell className="font-weight-bold text-right" style={{ width: '14%' }}>
                Capital ({curUnit})
              </TableCell>
              <TableCell className="font-weight-bold text-right" style={{ width: '14%' }}>
                Owner Equity ({curUnit})
              </TableCell>
              <TableCell className="font-weight-bold text-right" style={{ width: '14%' }}>
                Legal Reserve ({curUnit})
              </TableCell>
              <TableCell className="font-weight-bold text-right" style={{ width: '14%' }}>
                Retained Earning ({curUnit})
              </TableCell>
              <TableCell className="font-weight-bold text-right" style={{ width: '14%' }}>
                Total ({curUnit})
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className="text-left font-weight-bold">
                Balance at {this.getStartDateLabel(this.state.priorFyfrom) || `January 01, ${priorLabel || 'Prior Year'}`}
              </TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(priorData.capitalTotal)}
              </TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(priorData.capitalTotal)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="text-left">Net profit for the year ({priorLabel})</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(this.state.priorPl.netProfit)}
              </TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(this.state.priorPl.netProfit)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="text-left">Partner's current account / Equity</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(priorData.ownerEquityAmount)}
              </TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(priorData.ownerEquityAmount)}
              </TableCell>
            </TableRow>

            <TableRow style={{ borderTop: '1px solid #444', borderBottom: '2px solid #444', backgroundColor: '#fcfcfc' }}>
              <TableCell className="text-left font-weight-bold">
                Balance at {this.getEndDateLabel(this.state.priorFyto, this.state.priorFyfrom) || `December 31, ${priorLabel || 'Prior Year'}`}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(priorData.capitalTotal)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(priorData.ownerEquityAmount)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(priorData.statutoryReserve)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(priorData.retainedEarnings)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(priorData.totalPartnersEquity)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="text-left">Net profit for the year ({currLabel})</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(this.state.currPl.netProfit)}
              </TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(this.state.currPl.netProfit)}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell className="text-left">Net Drawings in Partners Current</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(
                  currData.ownerEquityAmount - priorData.ownerEquityAmount
                )}
              </TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">-</TableCell>
              <TableCell className="text-right">
                {this.renderAccountingValue(
                  currData.ownerEquityAmount - priorData.ownerEquityAmount
                )}
              </TableCell>
            </TableRow>

            <TableRow style={{ borderTop: '2px solid #000', borderBottom: '3px double #000', backgroundColor: '#f5f5f5' }}>
              <TableCell className="text-left font-weight-bold">
                Balance at {this.getEndDateLabel(this.state.fyto, this.state.fyfrom) || `December 31, ${currLabel || 'Current Year'}`}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(currData.capitalTotal)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(currData.ownerEquityAmount)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(currData.statutoryReserve)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(currData.retainedEarnings)}
              </TableCell>
              <TableCell className="text-right font-weight-bold">
                {this.renderAccountingValue(currData.totalPartnersEquity)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div className="text-center text-muted mt-4 small font-italic">
          The accompanying notes form an integral part of these financial statements.
          <div className="mt-1">-2-</div>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------
   * Render Tab 3: Statement of Cash Flows (Page 3 Template)
   * ------------------------------------------------------------- */
  renderCashFlowStatement(currData, priorData) {
    const curUnit = currency || 'QR.';
    const currYearLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
    const priorYearLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);

    const twoYearsPriorData =
      this.state.twoYearsPriorAssets && this.state.twoYearsPriorAssets.length > 0
        ? this.classifyFinancialData(
            this.state.twoYearsPriorAssets,
            this.state.twoYearsPriorLiabilities,
            this.state.twoYearsPriorPl,
            this.state.twoYearsPriorProfitDist,
            this.state.twoYearsPriorPartnerCurrent
          )
        : null;

    const currCf = this.computeCashFlowStatement(currData, priorData);
    const priorCf = this.computeCashFlowStatement(priorData, twoYearsPriorData);

    const renderNoteBadge = (num) => (
      <span
        onClick={() => this.navigateToNote(num)}
        style={{
          cursor: 'pointer',
          padding: '2px 8px',
          borderRadius: '4px',
          backgroundColor: '#f0f4f8',
          color: '#1a56db',
          fontWeight: 'bold',
          fontSize: '11px',
          border: '1px solid #d0e0fc'
        }}
        title={`View Note ${num} Breakdown`}
      >
        {num}
      </span>
    );

    return (
      <div className="p-3 bg-white" style={{ maxWidth: '960px', margin: '0 auto', fontFamily: 'serif' }}>
        {/* Header Block */}
        <div className="text-center mb-4">
          <h4 className="font-weight-bold mb-1" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
            {clientname || 'COMPANY NAME'}
          </h4>
          <div className="text-muted small mb-1">With Limited Liabilities</div>
          <div className="text-muted small font-weight-bold mb-2">
            {ccity ? `${ccity} - ` : ''}
            {ccountry || ''}
          </div>
          <h5 className="font-weight-bold text-dark mb-1" style={{ textDecoration: 'underline' }}>
            STATEMENT OF CASH FLOWS FOR THE YEAR ENDED {this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}
          </h5>
        </div>

        <div>
          <Table className="table table-sm table-borderless" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TableHead>
              <TableRow style={{ borderBottom: '2px solid #000' }}>
                <TableCell className="font-weight-bold text-left p-2" style={{ width: '50%' }}>
                  Particulars
                </TableCell>
                <TableCell className="font-weight-bold text-center p-2" style={{ width: '10%' }}>
                  Note
                </TableCell>
                <TableCell className="font-weight-bold text-right p-2" style={{ width: '20%' }}>
                  <div>{currYearLabel || 'Current'}</div>
                  <div className="small font-weight-normal">{curUnit}</div>
                </TableCell>
                <TableCell className="font-weight-bold text-right p-2" style={{ width: '20%' }}>
                  <div>{priorYearLabel || 'Prior'}</div>
                  <div className="small font-weight-normal">{curUnit}</div>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {CASH_FLOW_LINE_ITEMS.map((item, idx) => {
                if (item.type === 'header') {
                  return (
                    <TableRow key={`hdr-${idx}`}>
                      <TableCell colSpan={4} className="font-weight-bold text-left pt-3 pb-1" style={{ fontSize: '14px' }}>
                        {item.label}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (item.type === 'divider') {
                  return (
                    <TableRow key={`div-${idx}`}>
                      <TableCell colSpan={4} style={{ height: '6px', padding: 0 }} />
                    </TableRow>
                  );
                }
                if (item.isSubtotal) {
                  return (
                    <TableRow key={`sub-${item.key}`} style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777' }}>
                      <TableCell className="text-left pl-3 font-weight-bold py-1">{item.label}</TableCell>
                      <TableCell className="text-center py-1" />
                      <TableCell className="text-right font-weight-bold py-1">
                        {this.renderAccountingValue(currCf[item.key], true)}
                      </TableCell>
                      <TableCell className="text-right font-weight-bold py-1">
                        {this.renderAccountingValue(priorCf[item.key], true)}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (item.isTotal) {
                  return (
                    <TableRow key={`tot-${item.key}`} style={{ borderTop: '1px solid #777', borderBottom: '1px solid #777', backgroundColor: '#fafafa' }}>
                      <TableCell className="text-left pl-3 font-weight-bold py-1">{item.label}</TableCell>
                      <TableCell className="text-center py-1" />
                      <TableCell className="text-right font-weight-bold py-1">
                        {this.renderAccountingValue(currCf[item.key], true)}
                      </TableCell>
                      <TableCell className="text-right font-weight-bold py-1">
                        {this.renderAccountingValue(priorCf[item.key], true)}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (item.isDoubleUnderline) {
                  return (
                    <TableRow key={`dbl-${item.key}`} style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#f5f7fa' }}>
                      <TableCell className="text-left pl-3 font-weight-bold py-2">{item.label}</TableCell>
                      <TableCell className="text-center py-2">
                        {item.note ? renderNoteBadge(item.note) : null}
                      </TableCell>
                      <TableCell className="text-right font-weight-bold py-2">
                        {this.renderAccountingValue(currCf[item.key], true)}
                      </TableCell>
                      <TableCell className="text-right font-weight-bold py-2">
                        {this.renderAccountingValue(priorCf[item.key], true)}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (item.isVariance) {
                  const hasVar = Math.abs(currCf[item.key] || 0) > 0.01;
                  return (
                    <TableRow key={`var-${item.key}`} style={{ borderBottom: '1px solid #ccc', backgroundColor: hasVar ? '#fff3cd' : 'transparent' }}>
                      <TableCell className="text-left pl-3 font-weight-bold py-1" style={{ color: hasVar ? '#856404' : 'inherit' }}>
                        {item.label}
                      </TableCell>
                      <TableCell className="text-center py-1" />
                      <TableCell className="text-right font-weight-bold py-1" style={{ color: hasVar ? '#856404' : 'inherit' }}>
                        {this.renderAccountingValue(currCf[item.key], true)}
                      </TableCell>
                      <TableCell className="text-right font-weight-bold py-1">
                        {this.renderAccountingValue(priorCf[item.key], true)}
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={`row-${item.key}`}>
                    <TableCell className="text-left pl-4 py-1">{item.label}</TableCell>
                    <TableCell className="text-center py-1">
                      {item.note ? renderNoteBadge(item.note) : null}
                    </TableCell>
                    <TableCell className="text-right py-1">
                      {this.renderAccountingValue(currCf[item.key])}
                    </TableCell>
                    <TableCell className="text-right py-1">
                      {this.renderAccountingValue(priorCf[item.key])}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Dynamic Cash Reconciliation Variance Audit Note */}
          {Math.abs(currCf.variance || 0) > 0.01 && (
            <div
              className="mt-3 p-3 rounded text-left"
              style={{
                borderLeft: '4px solid #f59e0b',
                backgroundColor: '#fffbeb',
                border: '1px solid #fef3c7',
                color: '#92400e'
              }}
            >
              <div className="font-weight-bold mb-1" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                CASH RECONCILIATION AUDIT NOTE
              </div>
              <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                A variance of {curUnit} {this.renderAccountingValue(currCf.variance)} was detected between the net cash flow reconciliation ({this.renderAccountingValue(currCf.cashEndCF)} {curUnit}) and the closing cash and bank balances reported in the Statement of Financial Position ({this.renderAccountingValue(currCf.cashEndFP)} {curUnit}). Please verify non-cash journal entries, direct equity adjustments, or opening balance movements for the period.
              </div>
            </div>
          )}

          <div className="text-center text-muted mt-4 small font-italic">
            The accompanying notes form an integral part of these financial statements.
            <div className="mt-1">-3-</div>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------
   * Render Tab 4: Notes to Financial Statements (Pages 4-8 Templates)
   * ------------------------------------------------------------- */
  renderNotesSection(currData, priorData) {
    const currYearLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
    const priorYearLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);
    const curUnit = currency || 'QR.';

    const fystart =
      this.state.fyfrom ||
      (this.state.selectedFy && this.state.selectedFy.from) ||
      financialyearstart;
    const fyend =
      this.state.fyto ||
      (this.state.selectedFy && this.state.selectedFy.to) ||
      financialyearend;
    const fystartFormatted = fystart ? moment(fystart).format("YYYY-MM-DD") : '';
    const fyendFormatted = fyend ? moment(fyend).format("YYYY-MM-DD") : '';

    const priorFystart =
      this.state.priorFyfrom ||
      (this.state.priorFy && this.state.priorFy.from);
    const priorFyend =
      this.state.priorFyto ||
      (this.state.priorFy && this.state.priorFy.to);
    const priorFystartFormatted = priorFystart ? moment(priorFystart).format("YYYY-MM-DD") : '';
    const priorFyendFormatted = priorFyend ? moment(priorFyend).format("YYYY-MM-DD") : '';

    const buildSearchQuery = (id, name, startD, endD) => {
      const params = new URLSearchParams();
      if (id) params.set('id', id);
      if (name) params.set('name', name);
      if (startD) {
        params.set('financialyearstart', startD);
        params.set('fromDate', startD);
      }
      if (endD) {
        params.set('financialyearend', endD);
        params.set('toDate', endD);
      }
      return `?${params.toString()}`;
    };

    const shareCapitalRows = this.getShareCapitalRows(currData, this.state.equityAllocations);
    const totalShareCapitalPercentage = shareCapitalRows.reduce(
      (sum, r) => sum + (Number(r.percentage) || 0),
      0
    );

    const renderNoteTable = (noteId, title, currItems, priorItems, currTotal, priorTotal) => {
      const itemMap = new Map();
      (currItems || []).forEach((c) => {
        const name = c.name || c.ledname;
        const id = c.id || c.ledid;
        const rule = c.rule;
        if (!itemMap.has(name)) itemMap.set(name, { id, name, rule, curr: 0, prior: 0 });
        itemMap.get(name).curr += Math.abs(Number(c.amount) || 0);
        if (id && !itemMap.get(name).id) itemMap.get(name).id = id;
        if (rule !== undefined && itemMap.get(name).rule === undefined) itemMap.get(name).rule = rule;
      });
      (priorItems || []).forEach((p) => {
        const name = p.name || p.ledname;
        const id = p.id || p.ledid;
        const rule = p.rule;
        if (!itemMap.has(name)) itemMap.set(name, { id, name, rule, curr: 0, prior: 0 });
        itemMap.get(name).prior += Math.abs(Number(p.amount) || 0);
        if (id && !itemMap.get(name).id) itemMap.get(name).id = id;
        if (rule !== undefined && itemMap.get(name).rule === undefined) itemMap.get(name).rule = rule;
      });

      const rows = Array.from(itemMap.values());

      return (
        <div id={noteId} className="mb-4 card shadow-sm p-3" style={{ border: '1px solid #e2e8f0' }}>
          <h6 className="font-weight-bold text-dark mb-2" style={{ textDecoration: 'underline' }}>
            {title}
          </h6>
          <Table className="table table-sm table-borderless m-0">
            <TableHead>
              <TableRow style={{ borderBottom: '1px solid #ccc' }}>
                <TableCell className="font-weight-bold text-left p-1">Particulars</TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {currYearLabel} ({curUnit})
                </TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {priorYearLabel} ({curUnit})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-left pl-3 py-1">
                      <Link
                        to={{
                          pathname: '/newledgerview',
                          search: buildSearchQuery(row.id, row.name, fystartFormatted, fyendFormatted)
                        }}
                        state={{
                          id: row.id,
                          ledgerid: row.id,
                          name: row.name,
                          ledgername: row.name,
                          rule: row.rule,
                          fromDate: fystartFormatted || fystart,
                          toDate: fyendFormatted || fyend,
                          financialyearstart: fystartFormatted || fystart,
                          financialyearend: fyendFormatted || fyend
                        }}
                        className="text-primary font-weight-bold"
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right py-1">
                      {row.id ? (
                        <Link
                          to={{
                            pathname: '/newledgerview',
                            search: buildSearchQuery(row.id, row.name, fystartFormatted, fyendFormatted)
                          }}
                          state={{
                            id: row.id,
                            ledgerid: row.id,
                            name: row.name,
                            ledgername: row.name,
                            rule: row.rule,
                            fromDate: fystartFormatted || fystart,
                            toDate: fyendFormatted || fyend,
                            financialyearstart: fystartFormatted || fystart,
                            financialyearend: fyendFormatted || fyend
                          }}
                          className="text-dark"
                          style={{ textDecoration: 'none' }}
                        >
                          {this.renderAccountingValue(row.curr)}
                        </Link>
                      ) : (
                        this.renderAccountingValue(row.curr)
                      )}
                    </TableCell>
                    <TableCell className="text-right py-1">
                      {row.id && priorFystartFormatted ? (
                        <Link
                          to={{
                            pathname: '/newledgerview',
                            search: buildSearchQuery(row.id, row.name, priorFystartFormatted, priorFyendFormatted)
                          }}
                          state={{
                            id: row.id,
                            ledgerid: row.id,
                            name: row.name,
                            ledgername: row.name,
                            rule: row.rule,
                            fromDate: priorFystartFormatted || priorFystart,
                            toDate: priorFyendFormatted || priorFyend,
                            financialyearstart: priorFystartFormatted || priorFystart,
                            financialyearend: priorFyendFormatted || priorFyend
                          }}
                          className="text-dark"
                          style={{ textDecoration: 'none' }}
                        >
                          {this.renderAccountingValue(row.prior)}
                        </Link>
                      ) : (
                        this.renderAccountingValue(row.prior)
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell className="text-left pl-3 py-1 text-muted font-italic">
                    No individual ledger records found
                  </TableCell>
                  <TableCell className="text-right py-1">
                    {this.renderAccountingValue(currTotal)}
                  </TableCell>
                  <TableCell className="text-right py-1">
                    {this.renderAccountingValue(priorTotal)}
                  </TableCell>
                </TableRow>
              )}
              <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
                <TableCell className="text-left font-weight-bold py-1">Total</TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(currTotal, true)}
                </TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(priorTotal, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      );
    };

    return (
      <div className="p-3 bg-white" style={{ maxWidth: '960px', margin: '0 auto', fontFamily: 'serif' }}>
        <div className="text-center mb-4">
          <h4 className="font-weight-bold mb-1" style={{ letterSpacing: '1px', textTransform: 'uppercase' }}>
            {clientname || 'COMPANY NAME'}
          </h4>
          <div className="text-muted small mb-1">With Limited Liabilities</div>
          <div className="text-muted small font-weight-bold mb-2">
            {ccity ? `${ccity} - ` : ''}
            {ccountry || ''}
          </div>
          <h5 className="font-weight-bold text-dark mb-1" style={{ textDecoration: 'underline' }}>
            NOTES TO THE FINANCIAL STATEMENTS FOR THE YEAR ENDED {this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}
          </h5>
        </div>

        {/* Note 3: Fixed Assets Net */}
        <div id="note-3" className="mb-4 card shadow-sm p-3" style={{ border: '1px solid #e2e8f0' }}>
          <h6 className="font-weight-bold text-dark mb-2" style={{ textDecoration: 'underline' }}>
            3. FIXED ASSETS, NET
          </h6>
          <Table className="table table-sm table-bordered m-0 text-center">
            <TableHead className="thead-light">
              <TableRow>
                <TableCell className="text-left font-weight-bold">Particulars</TableCell>
                <TableCell className="font-weight-bold">Machinery & Equipment</TableCell>
                <TableCell className="font-weight-bold">Computer & Accessories</TableCell>
                <TableCell className="font-weight-bold">Motor Vehicles</TableCell>
                <TableCell className="font-weight-bold">Office Furniture & Fixtures</TableCell>
                <TableCell className="font-weight-bold">Total ({curUnit})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className="text-left font-weight-bold" colSpan={6}>
                  Cost:
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3">
                  Balance as of {this.getStartDateLabel(this.state.fyfrom)}
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>{this.renderAccountingValue(priorData.totalPropertyEquipment)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3">Additions / (Disposals)</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  {this.renderAccountingValue(
                    currData.totalPropertyEquipment - priorData.totalPropertyEquipment
                  )}
                </TableCell>
              </TableRow>
              <TableRow style={{ borderTop: '1px solid #333', borderBottom: '2px solid #333', backgroundColor: '#fafafa' }}>
                <TableCell className="text-left pl-3 font-weight-bold">
                  Net Book Value as of {this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell className="font-weight-bold">
                  {this.renderAccountingValue(currData.totalPropertyEquipment, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Note 4: Cash and Bank */}
        {renderNoteTable(
          'note-4',
          '4. Cash on Hand and at Banks',
          currData.cashAndBanks,
          priorData.cashAndBanks,
          currData.totalCashAndBanks,
          priorData.totalCashAndBanks
        )}

        {/* Note 5: Account Receivables */}
        {renderNoteTable(
          'note-5',
          '5. Accounts Receivables',
          currData.accountReceivables,
          priorData.accountReceivables,
          currData.totalAccountReceivables,
          priorData.totalAccountReceivables
        )}

        {/* Note 6: Inventory */}
        {renderNoteTable(
          'note-6',
          '6. Inventory',
          currData.inventory,
          priorData.inventory,
          currData.totalInventory,
          priorData.totalInventory
        )}

        {/* Note 7: Other Debit Balances */}
        {renderNoteTable(
          'note-7',
          '7. Other Debit Balances',
          currData.otherDebitBalances,
          priorData.otherDebitBalances,
          currData.totalOtherDebitBalances,
          priorData.totalOtherDebitBalances
        )}

        {/* Note 8: Accounts Payables */}
        {renderNoteTable(
          'note-8',
          '8. Accounts Payables',
          currData.accountsPayable,
          priorData.accountsPayable,
          currData.totalAccountsPayable,
          priorData.totalAccountsPayable
        )}

        {/* Note 9: Other Current Liabilities */}
        {renderNoteTable(
          'note-9',
          '9. Other Current Liabilities',
          currData.otherCreditBalances,
          priorData.otherCreditBalances,
          currData.totalOtherCreditBalances,
          priorData.totalOtherCreditBalances
        )}

        {/* Note 10: Share Capital */}
        <div id="note-10" className="mb-4 card shadow-sm p-3" style={{ border: '1px solid #e2e8f0' }}>
          <h6 className="font-weight-bold text-dark mb-2" style={{ textDecoration: 'underline' }}>
            10. Share Capital
          </h6>
          <Table className="table table-sm table-borderless m-0">
            <TableHead>
              <TableRow style={{ borderBottom: '1px solid #ccc' }}>
                <TableCell className="font-weight-bold text-left p-1">Partner / Shareholder</TableCell>
                <TableCell className="font-weight-bold text-center p-1" style={{ width: '120px' }}>
                  Percentage
                </TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  Amount ({curUnit})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shareCapitalRows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-left pl-3 py-1">
                    {row.isLink ? (
                      <Link
                        to={{
                          pathname: '/newledgerview',
                          search: buildSearchQuery(row.id, row.partnerName, fystartFormatted, fyendFormatted)
                        }}
                        state={{
                          id: row.id,
                          ledgerid: row.id,
                          name: row.partnerName,
                          ledgername: row.partnerName,
                          rule: row.rule !== undefined ? row.rule : 0,
                          fromDate: fystartFormatted || fystart,
                          toDate: fyendFormatted || fyend,
                          financialyearstart: fystartFormatted || fystart,
                          financialyearend: fyendFormatted || fyend
                        }}
                        className="text-primary font-weight-bold"
                      >
                        {row.partnerName}
                      </Link>
                    ) : (
                      row.partnerName
                    )}
                  </TableCell>
                  <TableCell className="text-center py-1">
                    {this.formatPercentage(row.percentage)}
                  </TableCell>
                  <TableCell className="text-right py-1">
                    {row.isLink ? (
                      <Link
                        to={{
                          pathname: '/newledgerview',
                          search: buildSearchQuery(row.id, row.partnerName, fystartFormatted, fyendFormatted)
                        }}
                        state={{
                          id: row.id,
                          ledgerid: row.id,
                          name: row.partnerName,
                          ledgername: row.partnerName,
                          rule: row.rule !== undefined ? row.rule : 0,
                          fromDate: fystartFormatted || fystart,
                          toDate: fyendFormatted || fyend,
                          financialyearstart: fystartFormatted || fystart,
                          financialyearend: fyendFormatted || fyend
                        }}
                        className="text-dark"
                        style={{ textDecoration: 'none' }}
                      >
                        {this.renderAccountingValue(row.amount)}
                      </Link>
                    ) : (
                      this.renderAccountingValue(row.amount)
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
                <TableCell className="text-left font-weight-bold py-1">Total</TableCell>
                <TableCell className="text-center font-weight-bold py-1">
                  {this.formatPercentage(totalShareCapitalPercentage || 100)}
                </TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(currData.capitalTotal, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Note 11: Retained Earnings */}
        <div id="note-11" className="mb-4 card shadow-sm p-3" style={{ border: '1px solid #e2e8f0' }}>
          <h6 className="font-weight-bold text-dark mb-2" style={{ textDecoration: 'underline' }}>
            11. Retained Earnings
          </h6>
          <Table className="table table-sm table-borderless m-0">
            <TableHead>
              <TableRow style={{ borderBottom: '1px solid #ccc' }}>
                <TableCell className="font-weight-bold text-left p-1">Particulars</TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {currYearLabel} ({curUnit})
                </TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {priorYearLabel} ({curUnit})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Opening Balance</TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(priorData.retainedEarnings)}
                </TableCell>
                <TableCell className="text-right py-1">-</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Add: Current Year Profit / (Loss)</TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(this.state.currPl.netProfit)}
                </TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(this.state.priorPl.netProfit)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Less: Transfer to legal reserve</TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(currData.statutoryReserve ? -currData.statutoryReserve : 0)}
                </TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(priorData.statutoryReserve ? -priorData.statutoryReserve : 0)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Less: Withdrawals as Dividend</TableCell>
                <TableCell className="text-right py-1">-</TableCell>
                <TableCell className="text-right py-1">-</TableCell>
              </TableRow>
              <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
                <TableCell className="text-left font-weight-bold py-1">Total</TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(currData.retainedEarnings, true)}
                </TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(priorData.retainedEarnings, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Note 12: Legal Reserve */}
        <div id="note-12" className="mb-4 card shadow-sm p-3" style={{ border: '1px solid #e2e8f0' }}>
          <h6 className="font-weight-bold text-dark mb-2" style={{ textDecoration: 'underline' }}>
            12. Legal Reserve
          </h6>
          <Table className="table table-sm table-borderless m-0">
            <TableHead>
              <TableRow style={{ borderBottom: '1px solid #ccc' }}>
                <TableCell className="font-weight-bold text-left p-1">Particulars</TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {currYearLabel} ({curUnit})
                </TableCell>
                <TableCell className="font-weight-bold text-right p-1" style={{ width: '130px' }}>
                  {priorYearLabel} ({curUnit})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Opening Balance</TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(priorData.statutoryReserve)}
                </TableCell>
                <TableCell className="text-right py-1">-</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-left pl-3 py-1">Add: Transfer from retained earnings</TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(
                    currData.statutoryReserve - priorData.statutoryReserve
                  )}
                </TableCell>
                <TableCell className="text-right py-1">
                  {this.renderAccountingValue(priorData.statutoryReserve)}
                </TableCell>
              </TableRow>
              <TableRow style={{ borderTop: '1px solid #000', borderBottom: '3px double #000', backgroundColor: '#fafafa' }}>
                <TableCell className="text-left font-weight-bold py-1">Total</TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(currData.statutoryReserve, true)}
                </TableCell>
                <TableCell className="text-right font-weight-bold py-1">
                  {this.renderAccountingValue(priorData.statutoryReserve, true)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="text-center text-muted mt-4 small font-italic">
          The accompanying notes form an integral part of these financial statements.
          <div className="mt-1">-3-</div>
        </div>
      </div>
    );
  }

  buildNoteRows(currItems, priorItems, currTotal, priorTotal) {
    const itemMap = new Map();
    (currItems || []).forEach((c) => {
      const name = c.name || c.ledname;
      if (!itemMap.has(name)) itemMap.set(name, { name, curr: 0, prior: 0 });
      itemMap.get(name).curr += Math.abs(Number(c.amount) || 0);
    });
    (priorItems || []).forEach((p) => {
      const name = p.name || p.ledname;
      if (!itemMap.has(name)) itemMap.set(name, { name, curr: 0, prior: 0 });
      itemMap.get(name).prior += Math.abs(Number(p.amount) || 0);
    });

    const rows = [];
    itemMap.forEach((val) => {
      rows.push([val.name, val.curr, val.prior]);
    });

    return { rows, currTotal, priorTotal };
  }

  /* -------------------------------------------------------------
   * Export Selected Sections to Excel (.xlsx)
   * ------------------------------------------------------------- */
  exportToExcel = () => {
    const { exportSections, equityAllocations } = this.state;
    const { financialPosition, changesInEquity, cashFlow, notes } = exportSections;

    if (!financialPosition && !changesInEquity && !cashFlow && !notes) {
      alert('Please select at least one section checkbox to export.');
      return;
    }

    try {
      const currData = this.classifyFinancialData(
        this.state.currAssets,
        this.state.currLiabilities,
        this.state.currPl,
        this.state.currProfitDist,
        this.state.currPartnerCurrent
      );
      const priorData = this.classifyFinancialData(
        this.state.priorAssets,
        this.state.priorLiabilities,
        this.state.priorPl,
        this.state.priorProfitDist,
        this.state.priorPartnerCurrent
      );
      const twoYearsPriorData =
        this.state.twoYearsPriorAssets && this.state.twoYearsPriorAssets.length > 0
          ? this.classifyFinancialData(
              this.state.twoYearsPriorAssets,
              this.state.twoYearsPriorLiabilities,
              this.state.twoYearsPriorPl,
              this.state.twoYearsPriorProfitDist,
              this.state.twoYearsPriorPartnerCurrent
            )
          : null;

      const currYearLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
      const priorYearLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);
      const curUnit = currency || 'QR.';
      const compName = clientname || 'COMPANY NAME';
      const compLoc = `${ccity ? `${ccity} - ` : ''}${ccountry || ''}`;
      const endDate = this.getEndDateLabel(this.state.fyto, this.state.fyfrom);

      const fmt = (val) => {
        if (val === null || val === undefined || val === '') return '-';
        const n = Number(val);
        if (isNaN(n) || n === 0) return '-';
        if (n < 0) return `(${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      const wb = XLSX.utils.book_new();
      let pageNumber = 1;

      // ==========================================
      // SHEET 1: Statement of Financial Position
      // ==========================================
      if (financialPosition) {
        const s1 = [];
        s1.push([compName.toUpperCase(), '', '', '']);
        s1.push(['With Limited Liabilities', '', '', '']);
        s1.push([compLoc, '', '', '']);
        s1.push([`FINANCIAL POSITION AS OF ${endDate}`, '', '', '']);
        s1.push(['', '', '', '']);
        s1.push(['Particulars', 'Note', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]);

        s1.push(['Assets:', '', '', '']);
        s1.push(['  Non-current Assets:', '', '', '']);
        s1.push(['    Property & Equipment', '3', fmt(currData.totalPropertyEquipment), fmt(priorData.totalPropertyEquipment)]);
        s1.push(['  Total Non-current Assets', '', fmt(currData.totalNonCurrentAssets), fmt(priorData.totalNonCurrentAssets)]);
        s1.push(['', '', '', '']);

        s1.push(['  Current Assets:', '', '', '']);
        s1.push(['    Cash on hand and at banks', '4', fmt(currData.totalCashAndBanks), fmt(priorData.totalCashAndBanks)]);
        s1.push(['    Account Receivables', '5', fmt(currData.totalAccountReceivables), fmt(priorData.totalAccountReceivables)]);
        s1.push(['    Inventory', '6', fmt(currData.totalInventory), fmt(priorData.totalInventory)]);
        s1.push(['    Other Debit Balances', '7', fmt(currData.totalOtherDebitBalances), fmt(priorData.totalOtherDebitBalances)]);
        s1.push(['    Due from related parties', '', fmt(currData.totalDueFromRelated), fmt(priorData.totalDueFromRelated)]);
        s1.push(['  Total Current Assets', '', fmt(currData.totalCurrentAssets), fmt(priorData.totalCurrentAssets)]);
        s1.push(['Total Assets', '', fmt(currData.totalAssets), fmt(priorData.totalAssets)]);
        s1.push(['', '', '', '']);

        s1.push(['Owner Equity and Liabilities', '', '', '']);
        s1.push(['  Owner Equity:', '', '', '']);
        s1.push(['    capital', '10', fmt(currData.capitalTotal), fmt(priorData.capitalTotal)]);
        s1.push(['    Statutory reserve', '11', fmt(currData.statutoryReserve), fmt(priorData.statutoryReserve)]);
        s1.push(['    Owner Equity', '', fmt(currData.ownerEquityAmount), fmt(priorData.ownerEquityAmount)]);
        s1.push(['    Retained earnings', '12', fmt(currData.retainedEarnings), fmt(priorData.retainedEarnings)]);
        s1.push(['  Total Partners\' Equity', '', fmt(currData.totalPartnersEquity), fmt(priorData.totalPartnersEquity)]);
        s1.push(['', '', '', '']);

        s1.push(['  NON-Current liabilities:', '', '', '']);
        s1.push(['    Long term loans', '', fmt(currData.totalLongTermLoans), fmt(priorData.totalLongTermLoans)]);
        s1.push(['  Total NON-Current Liabilities', '', fmt(currData.totalNonCurrentLiabilities), fmt(priorData.totalNonCurrentLiabilities)]);
        s1.push(['', '', '', '']);

        s1.push(['  Current liabilities:', '', '', '']);
        s1.push(['    Accounts Payable', '8', fmt(currData.totalAccountsPayable), fmt(priorData.totalAccountsPayable)]);
        s1.push(['    Other Credit Balances', '9', fmt(currData.totalOtherCreditBalances), fmt(priorData.totalOtherCreditBalances)]);
        s1.push(['    Due to related parties', '', fmt(currData.totalDueToRelated), fmt(priorData.totalDueToRelated)]);
        s1.push(['  Total Current Liabilities', '', fmt(currData.totalCurrentLiabilities), fmt(priorData.totalCurrentLiabilities)]);
        s1.push(['Total Partners\' Equity and Liabilities', '', fmt(currData.totalPartnersEquityAndLiabilities), fmt(priorData.totalPartnersEquityAndLiabilities)]);
        s1.push(['', '', '', '']);
        s1.push(['The accompanying notes form an integral part of these financial statements.', '', '', '']);
        s1.push([`-${pageNumber++}-`, '', '', '']);

        const ws1 = XLSX.utils.aoa_to_sheet(s1);
        ws1['!cols'] = [{ wch: 42 }, { wch: 8 }, { wch: 22 }, { wch: 22 }];
        XLSX.utils.book_append_sheet(wb, ws1, 'Financial Position');
      }

      // ===============================================
      // SHEET 2: Statement of Changes in Partners' Equity
      // ===============================================
      if (changesInEquity) {
        const s2 = [];
        s2.push([compName.toUpperCase(), '', '', '', '', '']);
        s2.push(['With Limited Liabilities', '', '', '', '', '']);
        s2.push([compLoc, '', '', '', '', '']);
        s2.push([`STATEMENT OF CHANGES IN PARTNERS' EQUITY FOR THE YEAR ENDED ${endDate}`, '', '', '', '', '']);
        s2.push(['', '', '', '', '', '']);
        s2.push([
          'Particulars',
          `Capital (${curUnit})`,
          `Owner Equity (${curUnit})`,
          `Legal Reserve (${curUnit})`,
          `Retained Earning (${curUnit})`,
          `Total (${curUnit})`
        ]);

        s2.push([
          `Balance at ${this.getStartDateLabel(this.state.priorFyfrom) || `January 01, ${priorYearLabel || 'Prior Year'}`}`,
          fmt(priorData.capitalTotal),
          '-',
          '-',
          '-',
          fmt(priorData.capitalTotal)
        ]);
        s2.push([
          `Net profit for the year (${priorYearLabel})`,
          '-',
          '-',
          '-',
          fmt(this.state.priorPl.netProfit),
          fmt(this.state.priorPl.netProfit)
        ]);
        s2.push([
          'Partner\'s current account / Equity',
          '-',
          fmt(priorData.ownerEquityAmount),
          '-',
          '-',
          fmt(priorData.ownerEquityAmount)
        ]);
        s2.push([
          `Balance at ${this.getEndDateLabel(this.state.priorFyto, this.state.priorFyfrom) || `December 31, ${priorYearLabel || 'Prior Year'}`}`,
          fmt(priorData.capitalTotal),
          fmt(priorData.ownerEquityAmount),
          fmt(priorData.statutoryReserve),
          fmt(priorData.retainedEarnings),
          fmt(priorData.totalPartnersEquity)
        ]);
        s2.push(['', '', '', '', '', '']);
        s2.push([
          `Net profit for the year (${currYearLabel})`,
          '-',
          '-',
          '-',
          fmt(this.state.currPl.netProfit),
          fmt(this.state.currPl.netProfit)
        ]);
        s2.push([
          'Net Drawings in Partners Current',
          '-',
          fmt(currData.ownerEquityAmount - priorData.ownerEquityAmount),
          '-',
          '-',
          fmt(currData.ownerEquityAmount - priorData.ownerEquityAmount)
        ]);
        s2.push([
          `Balance at ${this.getEndDateLabel(this.state.fyto, this.state.fyfrom) || `December 31, ${currYearLabel || 'Current Year'}`}`,
          fmt(currData.capitalTotal),
          fmt(currData.ownerEquityAmount),
          fmt(currData.statutoryReserve),
          fmt(currData.retainedEarnings),
          fmt(currData.totalPartnersEquity)
        ]);
        s2.push(['', '', '', '', '', '']);
        s2.push(['The accompanying notes form an integral part of these financial statements.', '', '', '', '', '']);
        s2.push([`-${pageNumber++}-`, '', '', '', '', '']);

        const ws2 = XLSX.utils.aoa_to_sheet(s2);
        ws2['!cols'] = [{ wch: 38 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws2, 'Changes in Equity');
      }

      // ==========================================
      // SHEET 3: Statement of Cash Flows
      // ==========================================
      if (cashFlow) {
        const currCf = this.computeCashFlowStatement(currData, priorData);
        const priorCf = this.computeCashFlowStatement(priorData, twoYearsPriorData);

        const sCf = [];
        sCf.push([compName.toUpperCase(), '', '', '']);
        sCf.push(['With Limited Liabilities', '', '', '']);
        sCf.push([compLoc, '', '', '']);
        sCf.push([`STATEMENT OF CASH FLOWS FOR THE YEAR ENDED ${endDate}`, '', '', '']);
        sCf.push(['', '', '', '']);
        sCf.push(['Particulars', 'Note', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]);

        CASH_FLOW_LINE_ITEMS.forEach((item) => {
          if (item.type === 'header') {
            sCf.push([item.label, '', '', '']);
          } else if (item.type === 'divider') {
            sCf.push(['', '', '', '']);
          } else {
            const indent = item.isTotal || item.isSubtotal || item.isVariance || item.isDoubleUnderline ? '  ' : '    ';
            sCf.push([
              `${indent}${item.label}`,
              item.note || '',
              fmt(currCf[item.key]),
              fmt(priorCf[item.key])
            ]);
          }
        });

        if (Math.abs(currCf.variance || 0) > 0.01) {
          sCf.push(['', '', '', '']);
          sCf.push(['CASH RECONCILIATION AUDIT NOTE:', '', '', '']);
          sCf.push([
            `A variance of ${curUnit} ${fmt(currCf.variance)} was detected between net cash flow reconciliation (${fmt(currCf.cashEndCF)} ${curUnit}) and closing cash on the Statement of Financial Position (${fmt(currCf.cashEndFP)} ${curUnit}).`,
            '',
            '',
            ''
          ]);
        }

        sCf.push(['', '', '', '']);
        sCf.push(['The accompanying notes form an integral part of these financial statements.', '', '', '']);
        sCf.push([`-${pageNumber++}-`, '', '', '']);

        const wsCf = XLSX.utils.aoa_to_sheet(sCf);
        wsCf['!cols'] = [{ wch: 55 }, { wch: 8 }, { wch: 22 }, { wch: 22 }];
        XLSX.utils.book_append_sheet(wb, wsCf, 'Statement of Cash Flows');
      }

      // ==========================================
      // SHEET 4: Notes to the Financial Statements
      // ==========================================
      if (notes) {
        const s3 = [];
        s3.push([compName.toUpperCase(), '', '', '', '', '']);
        s3.push(['With Limited Liabilities', '', '', '', '', '']);
        s3.push([compLoc, '', '', '', '', '']);
        s3.push([`NOTES TO THE FINANCIAL STATEMENTS FOR THE YEAR ENDED ${endDate}`, '', '', '', '', '']);
        s3.push(['', '', '', '', '', '']);

        const appendNoteToSheet = (title, currItems, priorItems, currTot, priorTot) => {
          s3.push([title, '', '']);
          s3.push(['Particulars', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]);
          const nData = this.buildNoteRows(currItems, priorItems, currTot, priorTot);
          if (nData.rows.length > 0) {
            nData.rows.forEach((r) => s3.push([`  ${r[0]}`, fmt(r[1]), fmt(r[2])]));
          } else {
            s3.push(['  No individual records found', fmt(currTot), fmt(priorTot)]);
          }
          s3.push(['Total', fmt(currTot), fmt(priorTot)]);
          s3.push(['', '', '']);
        };

        // Note 3: Fixed Assets
        s3.push(['3. FIXED ASSETS, NET', '', '', '', '', '']);
        s3.push(['Particulars', 'Machinery & Equip.', 'Computers', 'Vehicles', 'Furniture', `Total (${curUnit})`]);
        s3.push(['Cost:', '', '', '', '', '']);
        s3.push([`  Balance as of ${this.getStartDateLabel(this.state.fyfrom)}`, '-', '-', '-', '-', fmt(priorData.totalPropertyEquipment)]);
        s3.push(['  Additions / (Disposals)', '-', '-', '-', '-', fmt(currData.totalPropertyEquipment - priorData.totalPropertyEquipment)]);
        s3.push([`Net Book Value as of ${this.getEndDateLabel(this.state.fyto, this.state.fyfrom)}`, '-', '-', '-', '-', fmt(currData.totalPropertyEquipment)]);
        s3.push(['', '', '', '', '', '']);

        appendNoteToSheet('4. Cash on Hand and at Banks', currData.cashAndBanks, priorData.cashAndBanks, currData.totalCashAndBanks, priorData.totalCashAndBanks);
        appendNoteToSheet('5. Accounts Receivables', currData.accountReceivables, priorData.accountReceivables, currData.totalAccountReceivables, priorData.totalAccountReceivables);
        appendNoteToSheet('6. Inventory', currData.inventory, priorData.inventory, currData.totalInventory, priorData.totalInventory);
        appendNoteToSheet('7. Other Debit Balances', currData.otherDebitBalances, priorData.otherDebitBalances, currData.totalOtherDebitBalances, priorData.totalOtherDebitBalances);
        appendNoteToSheet('8. Accounts Payables', currData.accountsPayable, priorData.accountsPayable, currData.totalAccountsPayable, priorData.totalAccountsPayable);
        appendNoteToSheet('9. Other Current Liabilities', currData.otherCreditBalances, priorData.otherCreditBalances, currData.totalOtherCreditBalances, priorData.totalOtherCreditBalances);

        // Note 10: Share Capital with Actual Percentages
        s3.push(['10. Share Capital', '', '']);
        s3.push(['Partner / Shareholder', 'Percentage', `Amount (${curUnit})`]);
        const capRowsExcel = this.getShareCapitalRows(currData, equityAllocations);
        capRowsExcel.forEach((p) => {
          s3.push([`  ${p.partnerName}`, this.formatPercentage(p.percentage), fmt(p.amount)]);
        });
        const totalCapPctExcel = capRowsExcel.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
        s3.push(['Total', this.formatPercentage(totalCapPctExcel || 100), fmt(currData.capitalTotal)]);
        s3.push(['', '', '']);

        // Note 11: Retained Earnings
        s3.push(['11. Retained Earnings', '', '']);
        s3.push(['Particulars', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]);
        s3.push(['  Opening Balance', fmt(priorData.retainedEarnings), '-']);
        s3.push(['  Add: Current Year Profit / (Loss)', fmt(this.state.currPl.netProfit), fmt(this.state.priorPl.netProfit)]);
        s3.push(['  Less: Transfer to legal reserve', fmt(currData.statutoryReserve ? -currData.statutoryReserve : 0), fmt(priorData.statutoryReserve ? -priorData.statutoryReserve : 0)]);
        s3.push(['  Less: Withdrawals as Dividend', '-', '-']);
        s3.push(['Total', fmt(currData.retainedEarnings), fmt(priorData.retainedEarnings)]);
        s3.push(['', '', '']);

        // Note 12: Legal Reserve
        s3.push(['12. Legal Reserve', '', '']);
        s3.push(['Particulars', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]);
        s3.push(['  Opening Balance', fmt(priorData.statutoryReserve), '-']);
        s3.push(['  Add: Transfer from retained earnings', fmt(currData.statutoryReserve - priorData.statutoryReserve), fmt(priorData.statutoryReserve)]);
        s3.push(['Total', fmt(currData.statutoryReserve), fmt(priorData.statutoryReserve)]);
        s3.push(['', '', '']);

        s3.push(['The accompanying notes form an integral part of these financial statements.', '', '']);
        s3.push([`-${pageNumber++}-`, '', '']);

        const ws3 = XLSX.utils.aoa_to_sheet(s3);
        ws3['!cols'] = [{ wch: 42 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws3, 'Notes to Accounts');
      }

      const fname = `Financial_Statements_${currYearLabel || 'Selected'}.xlsx`;
      XLSX.writeFile(wb, fname);
    } catch (e) {
      console.error('Failed to export Excel', e);
      alert('Export to Excel failed: ' + (e && e.message ? e.message : 'unknown'));
    }
  };

  /* -------------------------------------------------------------
   * Export Selected Sections to Multi-Page PDF
   * ------------------------------------------------------------- */
  exportToPDF = async () => {
    const { exportSections, equityAllocations } = this.state;
    const { financialPosition, changesInEquity, cashFlow, notes } = exportSections;

    if (!financialPosition && !changesInEquity && !cashFlow && !notes) {
      alert('Please select at least one section checkbox to export.');
      return;
    }

    try {
      const currData = this.classifyFinancialData(
        this.state.currAssets,
        this.state.currLiabilities,
        this.state.currPl,
        this.state.currProfitDist,
        this.state.currPartnerCurrent
      );
      const priorData = this.classifyFinancialData(
        this.state.priorAssets,
        this.state.priorLiabilities,
        this.state.priorPl,
        this.state.priorProfitDist,
        this.state.priorPartnerCurrent
      );
      const twoYearsPriorData =
        this.state.twoYearsPriorAssets && this.state.twoYearsPriorAssets.length > 0
          ? this.classifyFinancialData(
              this.state.twoYearsPriorAssets,
              this.state.twoYearsPriorLiabilities,
              this.state.twoYearsPriorPl,
              this.state.twoYearsPriorProfitDist,
              this.state.twoYearsPriorPartnerCurrent
            )
          : null;

      const currYearLabel = this.getYearLabel(this.state.fyfrom, this.state.fyto);
      const priorYearLabel = this.getYearLabel(this.state.priorFyfrom, this.state.priorFyto);
      const curUnit = currency || 'QR.';
      const compName = clientname || 'COMPANY NAME';
      const compLoc = `${ccity ? `${ccity} - ` : ''}${ccountry || ''}`;
      const endDate = this.getEndDateLabel(this.state.fyto, this.state.fyfrom);

      const doc = new jsPDF('p', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginLeft = 36;
      let pageNumber = 1;
      let hasAddedFirstPage = false;

      const fmt = (val) => {
        if (val === null || val === undefined || val === '') return '-';
        const n = Number(val);
        if (isNaN(n) || n === 0) return '-';
        if (n < 0) return `(${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      const addHeader = (title) => {
        let y = 36;
        doc.setFontSize(13);
        doc.setFont('times', 'bold');
        doc.text(compName.toUpperCase(), pageWidth / 2, y, { align: 'center' });
        y += 14;

        doc.setFontSize(9.5);
        doc.setFont('times', 'normal');
        doc.text('With Limited Liabilities', pageWidth / 2, y, { align: 'center' });
        y += 12;

        doc.text(compLoc, pageWidth / 2, y, { align: 'center' });
        y += 15;

        doc.setFontSize(10.5);
        doc.setFont('times', 'bold');
        doc.text(title, pageWidth / 2, y, { align: 'center' });
        y += 20;
        return y;
      };

      // 1. Financial Position Page
      if (financialPosition) {
        if (hasAddedFirstPage) {
          doc.addPage();
        } else {
          hasAddedFirstPage = true;
        }

        let cursorY1 = addHeader(`FINANCIAL POSITION AS OF ${endDate}`);

        const body1 = [
          ['Assets:', '', '', ''],
          ['Non-current Assets:', '', '', ''],
          ['Property & Equipment', '3', fmt(currData.totalPropertyEquipment), fmt(priorData.totalPropertyEquipment)],
          ['Total Non-current Assets', '', fmt(currData.totalNonCurrentAssets), fmt(priorData.totalNonCurrentAssets)],
          ['', '', '', ''],
          ['Current Assets:', '', '', ''],
          ['Cash on hand and at banks', '4', fmt(currData.totalCashAndBanks), fmt(priorData.totalCashAndBanks)],
          ['Account Receivables', '5', fmt(currData.totalAccountReceivables), fmt(priorData.totalAccountReceivables)],
          ['Inventory', '6', fmt(currData.totalInventory), fmt(priorData.totalInventory)],
          ['Other Debit Balances', '7', fmt(currData.totalOtherDebitBalances), fmt(priorData.totalOtherDebitBalances)],
          ['Due from related parties', '', fmt(currData.totalDueFromRelated), fmt(priorData.totalDueFromRelated)],
          ['Total Current Assets', '', fmt(currData.totalCurrentAssets), fmt(priorData.totalCurrentAssets)],
          ['Total Assets', '', fmt(currData.totalAssets), fmt(priorData.totalAssets)],
          ['', '', '', ''],
          ['Owner Equity and Liabilities', '', '', ''],
          ['Owner Equity:', '', '', ''],
          ['capital', '10', fmt(currData.capitalTotal), fmt(priorData.capitalTotal)],
          ['Statutory reserve', '11', fmt(currData.statutoryReserve), fmt(priorData.statutoryReserve)],
          ['Owner Equity', '', fmt(currData.ownerEquityAmount), fmt(priorData.ownerEquityAmount)],
          ['Retained earnings', '12', fmt(currData.retainedEarnings), fmt(priorData.retainedEarnings)],
          ['Total Partners\' Equity', '', fmt(currData.totalPartnersEquity), fmt(priorData.totalPartnersEquity)],
          ['', '', '', ''],
          ['NON-Current liabilities:', '', '', ''],
          ['Long term loans', '', fmt(currData.totalLongTermLoans), fmt(priorData.totalLongTermLoans)],
          ['Total NON-Current Liabilities', '', fmt(currData.totalNonCurrentLiabilities), fmt(priorData.totalNonCurrentLiabilities)],
          ['', '', '', ''],
          ['Current liabilities:', '', '', ''],
          ['Accounts Payable', '8', fmt(currData.totalAccountsPayable), fmt(priorData.totalAccountsPayable)],
          ['Other Credit Balances', '9', fmt(currData.totalOtherCreditBalances), fmt(priorData.totalOtherCreditBalances)],
          ['Due to related parties', '', fmt(currData.totalDueToRelated), fmt(priorData.totalDueToRelated)],
          ['Total Current Liabilities', '', fmt(currData.totalCurrentLiabilities), fmt(priorData.totalCurrentLiabilities)],
          ['Total Partners\' Equity and Liabilities', '', fmt(currData.totalPartnersEquityAndLiabilities), fmt(priorData.totalPartnersEquityAndLiabilities)]
        ];

        doc.autoTable({
          head: [['Particulars', 'Note', `${currYearLabel}\n(${curUnit})`, `${priorYearLabel}\n(${curUnit})`]],
          body: body1,
          startY: cursorY1,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 9, cellPadding: 2.2 },
          headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
          columnStyles: {
            0: { cellWidth: 260, halign: 'left' },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 110, halign: 'right' },
            3: { cellWidth: 110, halign: 'right' }
          },
          didParseCell: (data) => {
            const raw = data.row.raw && data.row.raw[0];
            if (
              raw === 'Assets:' ||
              raw === 'Non-current Assets:' ||
              raw === 'Current Assets:' ||
              raw === 'Owner Equity and Liabilities' ||
              raw === 'Owner Equity:' ||
              raw === 'NON-Current liabilities:' ||
              raw === 'Current liabilities:' ||
              raw === 'Total Non-current Assets' ||
              raw === 'Total Current Assets' ||
              raw === 'Total Partners\' Equity' ||
              raw === 'Total NON-Current Liabilities' ||
              raw === 'Total Current Liabilities'
            ) {
              data.cell.styles.fontStyle = 'bold';
            }
            if (raw === 'Total Assets' || raw === 'Total Partners\' Equity and Liabilities') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 248, 248];
            }
          },
          theme: 'plain'
        });

        let finalY1 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : cursorY1 + 450;
        doc.setFontSize(8.5);
        doc.setFont('times', 'italic');
        doc.text('The accompanying notes form an integral part of these financial statements.', pageWidth / 2, finalY1, { align: 'center' });
        doc.text(`-${pageNumber++}-`, pageWidth / 2, finalY1 + 11, { align: 'center' });
      }

      // 2. Changes in Equity Page
      if (changesInEquity) {
        if (hasAddedFirstPage) {
          doc.addPage();
        } else {
          hasAddedFirstPage = true;
        }

        let cursorY2 = addHeader(`STATEMENT OF CHANGES IN PARTNERS' EQUITY FOR THE YEAR ENDED ${endDate}`);

        const body2 = [
          [
            `Balance at ${this.getStartDateLabel(this.state.priorFyfrom) || `January 01, ${priorYearLabel || 'Prior Year'}`}`,
            fmt(priorData.capitalTotal),
            '-',
            '-',
            '-',
            fmt(priorData.capitalTotal)
          ],
          [
            `Net profit for the year (${priorYearLabel})`,
            '-',
            '-',
            '-',
            fmt(this.state.priorPl.netProfit),
            fmt(this.state.priorPl.netProfit)
          ],
          [
            'Partner\'s current account / Equity',
            '-',
            fmt(priorData.ownerEquityAmount),
            '-',
            '-',
            fmt(priorData.ownerEquityAmount)
          ],
          [
            `Balance at ${this.getEndDateLabel(this.state.priorFyto, this.state.priorFyfrom) || `December 31, ${priorYearLabel || 'Prior Year'}`}`,
            fmt(priorData.capitalTotal),
            fmt(priorData.ownerEquityAmount),
            fmt(priorData.statutoryReserve),
            fmt(priorData.retainedEarnings),
            fmt(priorData.totalPartnersEquity)
          ],
          ['', '', '', '', '', ''],
          [
            `Net profit for the year (${currYearLabel})`,
            '-',
            '-',
            '-',
            fmt(this.state.currPl.netProfit),
            fmt(this.state.currPl.netProfit)
          ],
          [
            'Net Drawings in Partners Current',
            '-',
            fmt(currData.ownerEquityAmount - priorData.ownerEquityAmount),
            '-',
            '-',
            fmt(currData.ownerEquityAmount - priorData.ownerEquityAmount)
          ],
          [
            `Balance at ${this.getEndDateLabel(this.state.fyto, this.state.fyfrom) || `December 31, ${currYearLabel || 'Current Year'}`}`,
            fmt(currData.capitalTotal),
            fmt(currData.ownerEquityAmount),
            fmt(currData.statutoryReserve),
            fmt(currData.retainedEarnings),
            fmt(currData.totalPartnersEquity)
          ]
        ];

        doc.autoTable({
          head: [[
            'Particulars',
            `Capital\n(${curUnit})`,
            `Owner Equity\n(${curUnit})`,
            `Legal Reserve\n(${curUnit})`,
            `Retained Earning\n(${curUnit})`,
            `Total\n(${curUnit})`
          ]],
          body: body2,
          startY: cursorY2,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 9, cellPadding: 4 },
          headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
          columnStyles: {
            0: { cellWidth: 170, halign: 'left' },
            1: { cellWidth: 70, halign: 'right' },
            2: { cellWidth: 70, halign: 'right' },
            3: { cellWidth: 70, halign: 'right' },
            4: { cellWidth: 70, halign: 'right' },
            5: { cellWidth: 70, halign: 'right' }
          },
          didParseCell: (data) => {
            const raw = data.row.raw && data.row.raw[0];
            if (String(raw).includes('Balance at')) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 248, 248];
            }
          },
          theme: 'grid'
        });

        let finalY2 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 25 : cursorY2 + 250;
        doc.setFontSize(8.5);
        doc.setFont('times', 'italic');
        doc.text('The accompanying notes form an integral part of these financial statements.', pageWidth / 2, finalY2, { align: 'center' });
        doc.text(`-${pageNumber++}-`, pageWidth / 2, finalY2 + 11, { align: 'center' });
      }

      // 3. Statement of Cash Flows Page
      if (cashFlow) {
        if (hasAddedFirstPage) {
          doc.addPage();
        } else {
          hasAddedFirstPage = true;
        }

        let cursorY3 = addHeader(`STATEMENT OF CASH FLOWS FOR THE YEAR ENDED ${endDate}`);

        const currCf = this.computeCashFlowStatement(currData, priorData);
        const priorCf = this.computeCashFlowStatement(priorData, twoYearsPriorData);

        const bodyCf = [];
        CASH_FLOW_LINE_ITEMS.forEach((item) => {
          if (item.type === 'header') {
            bodyCf.push([item.label, '', '', '']);
          } else if (item.type === 'divider') {
            bodyCf.push(['', '', '', '']);
          } else {
            bodyCf.push([
              item.label,
              item.note || '',
              fmt(currCf[item.key]),
              fmt(priorCf[item.key])
            ]);
          }
        });

        doc.autoTable({
          head: [['Particulars', 'Note', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]],
          body: bodyCf,
          startY: cursorY3,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 7.5, cellPadding: 1.6 },
          headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' },
          columnStyles: {
            0: { cellWidth: 260, halign: 'left' },
            1: { cellWidth: 40, halign: 'center' },
            2: { cellWidth: 110, halign: 'right' },
            3: { cellWidth: 110, halign: 'right' }
          },
          didParseCell: (data) => {
            const raw = data.row.raw && data.row.raw[0];
            if (
              raw === 'CASH FLOWS FROM OPERATING ACTIVITIES' ||
              raw === 'CASH FLOWS FROM INVESTING ACTIVITIES' ||
              raw === 'CASH FLOWS FROM FINANCING ACTIVITIES' ||
              raw === 'Cash flow before working-capital changes' ||
              raw === 'NET CASH FROM OPERATING ACTIVITIES' ||
              raw === 'NET CASH FROM INVESTING ACTIVITIES' ||
              raw === 'NET CASH FROM FINANCING ACTIVITIES' ||
              raw === 'NET INCREASE / (DECREASE) IN CASH' ||
              raw === 'Cash and cash equivalents – end (cash-flow statement)' ||
              raw === 'CASH-FLOW VS FINANCIAL-POSITION VARIANCE'
            ) {
              data.cell.styles.fontStyle = 'bold';
            }
            if (
              raw === 'NET CASH FROM OPERATING ACTIVITIES' ||
              raw === 'NET CASH FROM INVESTING ACTIVITIES' ||
              raw === 'NET CASH FROM FINANCING ACTIVITIES' ||
              raw === 'NET INCREASE / (DECREASE) IN CASH' ||
              raw === 'Cash and cash equivalents – end (cash-flow statement)'
            ) {
              data.cell.styles.fillColor = [248, 248, 248];
            }
            if (raw === 'CASH-FLOW VS FINANCIAL-POSITION VARIANCE') {
              const val = data.row.raw[2];
              if (val && val !== '-' && val !== '0.00') {
                data.cell.styles.textColor = [180, 0, 0];
              }
            }
          },
          theme: 'plain'
        });

        let finalY3 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : cursorY3 + 450;

        if (Math.abs(currCf.variance || 0) > 0.01) {
          doc.setFontSize(7.5);
          doc.setFont('times', 'bold');
          doc.text('CASH RECONCILIATION AUDIT NOTE:', marginLeft, finalY3);
          finalY3 += 9;
          doc.setFont('times', 'normal');
          const varianceNotice = `A variance of ${curUnit} ${fmt(currCf.variance)} was detected between the net cash flow reconciliation (${fmt(currCf.cashEndCF)} ${curUnit}) and the closing cash and bank balances reported in the Statement of Financial Position (${fmt(currCf.cashEndFP)} ${curUnit}). Please verify non-cash journal entries, direct equity adjustments, or opening balance movements for the period.`;
          const splitControlText = doc.splitTextToSize(varianceNotice, pageWidth - marginLeft * 2);
          doc.text(splitControlText, marginLeft, finalY3);
          finalY3 += splitControlText.length * 8 + 6;
        }

        doc.setFontSize(8.5);
        doc.setFont('times', 'italic');
        doc.text('The accompanying notes form an integral part of these financial statements.', pageWidth / 2, finalY3, { align: 'center' });
        doc.text(`-${pageNumber++}-`, pageWidth / 2, finalY3 + 11, { align: 'center' });
      }

      // 4. Notes to Financial Statements Page(s)
      if (notes) {
        if (hasAddedFirstPage) {
          doc.addPage();
        } else {
          hasAddedFirstPage = true;
        }

        let cursorY3 = addHeader(`NOTES TO THE FINANCIAL STATEMENTS FOR THE YEAR ENDED ${endDate}`);

        const addNoteTableToPdf = (noteTitle, currItems, priorItems, currTot, priorTot) => {
          const nData = this.buildNoteRows(currItems, priorItems, currTot, priorTot);
          const rows = [];
          if (nData.rows.length > 0) {
            nData.rows.forEach((r) => rows.push([r[0], fmt(r[1]), fmt(r[2])]));
          } else {
            rows.push(['No individual records found', fmt(currTot), fmt(priorTot)]);
          }
          rows.push(['Total', fmt(currTot), fmt(priorTot)]);

          doc.autoTable({
            head: [[noteTitle, `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]],
            body: rows,
            startY: cursorY3,
            margin: { left: marginLeft, right: marginLeft },
            styles: { font: 'times', fontSize: 8.5, cellPadding: 2 },
            headStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' },
            columnStyles: {
              0: { cellWidth: 320, halign: 'left' },
              1: { cellWidth: 100, halign: 'right' },
              2: { cellWidth: 100, halign: 'right' }
            },
            didParseCell: (data) => {
              if (data.row.index === rows.length - 1) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [248, 248, 248];
              }
            },
            theme: 'plain'
          });

          cursorY3 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : cursorY3 + 60;
        };

        // Note 4 Cash and Bank
        addNoteTableToPdf('4. Cash on Hand and at Banks', currData.cashAndBanks, priorData.cashAndBanks, currData.totalCashAndBanks, priorData.totalCashAndBanks);

        // Note 5 Accounts Receivables
        addNoteTableToPdf('5. Accounts Receivables', currData.accountReceivables, priorData.accountReceivables, currData.totalAccountReceivables, priorData.totalAccountReceivables);

        // Note 6 Inventory
        addNoteTableToPdf('6. Inventory', currData.inventory, priorData.inventory, currData.totalInventory, priorData.totalInventory);

        // Note 7 Other Debit Balances
        addNoteTableToPdf('7. Other Debit Balances', currData.otherDebitBalances, priorData.otherDebitBalances, currData.totalOtherDebitBalances, priorData.totalOtherDebitBalances);

        // Note 8 Accounts Payables
        addNoteTableToPdf('8. Accounts Payables', currData.accountsPayable, priorData.accountsPayable, currData.totalAccountsPayable, priorData.totalAccountsPayable);

        // Note 9 Other Current Liabilities
        addNoteTableToPdf('9. Other Current Liabilities', currData.otherCreditBalances, priorData.otherCreditBalances, currData.totalOtherCreditBalances, priorData.totalOtherCreditBalances);

        // Note 10 Share Capital with Actual Percentages
        const capRowsPdf = [];
        const capRowsData = this.getShareCapitalRows(currData, equityAllocations);
        capRowsData.forEach((p) => {
          capRowsPdf.push([p.partnerName, this.formatPercentage(p.percentage), fmt(p.amount)]);
        });
        const totalCapPctPdf = capRowsData.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
        capRowsPdf.push(['Total', this.formatPercentage(totalCapPctPdf || 100), fmt(currData.capitalTotal)]);

        doc.autoTable({
          head: [['10. Share Capital', 'Percentage', `Amount (${curUnit})`]],
          body: capRowsPdf,
          startY: cursorY3,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 8.5, cellPadding: 2 },
          headStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' },
          columnStyles: {
            0: { cellWidth: 320, halign: 'left' },
            1: { cellWidth: 100, halign: 'center' },
            2: { cellWidth: 100, halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.row.index === capRowsPdf.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 248, 248];
            }
          },
          theme: 'plain'
        });
        cursorY3 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : cursorY3 + 60;

        // Note 11 Retained Earnings
        const retRows = [
          ['Opening Balance', fmt(priorData.retainedEarnings), '-'],
          ['Add: Current Year Profit / (Loss)', fmt(this.state.currPl.netProfit), fmt(this.state.priorPl.netProfit)],
          ['Less: Transfer to legal reserve', fmt(currData.statutoryReserve ? -currData.statutoryReserve : 0), fmt(priorData.statutoryReserve ? -priorData.statutoryReserve : 0)],
          ['Less: Withdrawals as Dividend', '-', '-'],
          ['Total', fmt(currData.retainedEarnings), fmt(priorData.retainedEarnings)]
        ];
        doc.autoTable({
          head: [['11. Retained Earnings', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]],
          body: retRows,
          startY: cursorY3,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 8.5, cellPadding: 2 },
          headStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' },
          columnStyles: {
            0: { cellWidth: 320, halign: 'left' },
            1: { cellWidth: 100, halign: 'right' },
            2: { cellWidth: 100, halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.row.index === retRows.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 248, 248];
            }
          },
          theme: 'plain'
        });
        cursorY3 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : cursorY3 + 60;

        // Note 12 Legal Reserve
        const legRows = [
          ['Opening Balance', fmt(priorData.statutoryReserve), '-'],
          ['Add: Transfer from retained earnings', fmt(currData.statutoryReserve - priorData.statutoryReserve), fmt(priorData.statutoryReserve)],
          ['Total', fmt(currData.statutoryReserve), fmt(priorData.statutoryReserve)]
        ];
        doc.autoTable({
          head: [['12. Legal Reserve', `${currYearLabel} (${curUnit})`, `${priorYearLabel} (${curUnit})`]],
          body: legRows,
          startY: cursorY3,
          margin: { left: marginLeft, right: marginLeft },
          styles: { font: 'times', fontSize: 8.5, cellPadding: 2 },
          headStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'left' },
          columnStyles: {
            0: { cellWidth: 320, halign: 'left' },
            1: { cellWidth: 100, halign: 'right' },
            2: { cellWidth: 100, halign: 'right' }
          },
          didParseCell: (data) => {
            if (data.row.index === legRows.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 248, 248];
            }
          },
          theme: 'plain'
        });

        let finalY3 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : cursorY3 + 40;
        doc.setFontSize(8.5);
        doc.setFont('times', 'italic');
        doc.text('The accompanying notes form an integral part of these financial statements.', pageWidth / 2, finalY3, { align: 'center' });
        doc.text(`-${pageNumber++}-`, pageWidth / 2, finalY3 + 11, { align: 'center' });
      }

      const fname = `Financial_Statements_${currYearLabel || 'Selected'}.pdf`;
      doc.save(fname);
    } catch (e) {
      console.error('Failed to export PDF', e);
      alert('Export to PDF failed: ' + (e && e.message ? e.message : 'unknown'));
    }
  };

  render() {
    const { loading, loader1, fylist, selectedFy, activeTab, exportSections } = this.state;

    const currData = this.classifyFinancialData(
      this.state.currAssets,
      this.state.currLiabilities,
      this.state.currPl,
      this.state.currProfitDist,
      this.state.currPartnerCurrent
    );
    const priorData = this.classifyFinancialData(
      this.state.priorAssets,
      this.state.priorLiabilities,
      this.state.priorPl,
      this.state.priorProfitDist,
      this.state.priorPartnerCurrent
    );

    const allSelected =
      exportSections.financialPosition &&
      exportSections.changesInEquity &&
      exportSections.cashFlow &&
      exportSections.notes;
    const noneSelected =
      !exportSections.financialPosition &&
      !exportSections.changesInEquity &&
      !exportSections.cashFlow &&
      !exportSections.notes;

    return (
      <div>
        <Header />
        <Menu />
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loader1}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <div className="content-wrapper">
          <div className="card card-info">
            <ol className="breadcrumb float-sm-right">
              <li className="breadcrumb-item text-secondary">
                <Link to="/accounts">Home</Link>
              </li>
              <li className="breadcrumb-item active text-backgroundColour">
                Statement of Financial Position
              </li>
            </ol>

            <div className="card-body">
              {/* Row 1: Controls & Selectors Bar */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center" style={{ minWidth: '320px' }}>
                  <Autocomplete
                    id="fylist-select"
                    className="fy-select mr-3"
                    style={{ width: '260px' }}
                    options={fylist || []}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      return this.getYearLabel(option.from, option.to);
                    }}
                    value={selectedFy || null}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      return this.getYearLabel(option.from, option.to) === this.getYearLabel(value.from, value.to);
                    }}
                    onChange={(e, value) => {
                      if (value) {
                        this.setState({ selectedFy: value });
                        this.onFySelect(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Financial Year"
                        variant="standard"
                        fullWidth
                      />
                    )}
                  />
                  {loading && <span className="ml-2">{spin}</span>}
                </div>

                {/* View Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newVal) => this.setState({ activeTab: newVal })}
                    textColor="primary"
                    indicatorColor="primary"
                  >
                    <Tab label="1. Financial Position" />
                    <Tab label="2. Changes in Equity" />
                    <Tab label="3. Statement of Cash Flows" />
                    <Tab label="4. Notes to Financial Statements" />
                  </Tabs>
                </Box>
              </div>

              {/* Row 2: Export Section Selection Checkboxes & Action Buttons */}
              <div
                className="d-flex flex-wrap justify-content-between align-items-center p-2 mb-3 rounded"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div className="d-flex flex-wrap align-items-center">
                  <Typography
                    variant="caption"
                    className="font-weight-bold mr-3 text-secondary"
                    style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                  >
                    Export Sections:
                  </Typography>
                  <FormGroup row className="align-items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={exportSections.financialPosition}
                          onChange={() => this.handleSectionToggle('financialPosition')}
                          color="primary"
                        />
                      }
                      label={<span style={{ fontSize: '13px', fontWeight: 500 }}>1. Financial Position</span>}
                      className="mr-3 mb-0"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={exportSections.changesInEquity}
                          onChange={() => this.handleSectionToggle('changesInEquity')}
                          color="primary"
                        />
                      }
                      label={<span style={{ fontSize: '13px', fontWeight: 500 }}>2. Changes in Equity</span>}
                      className="mr-3 mb-0"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={exportSections.cashFlow}
                          onChange={() => this.handleSectionToggle('cashFlow')}
                          color="primary"
                        />
                      }
                      label={<span style={{ fontSize: '13px', fontWeight: 500 }}>3. Statement of Cash Flows</span>}
                      className="mr-3 mb-0"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={exportSections.notes}
                          onChange={() => this.handleSectionToggle('notes')}
                          color="primary"
                        />
                      }
                      label={<span style={{ fontSize: '13px', fontWeight: 500 }}>4. Notes to Accounts</span>}
                      className="mr-3 mb-0"
                    />
                  </FormGroup>

                  <div className="btn-group btn-group-sm ml-2">
                    <Chip
                      label={allSelected ? 'Deselect All' : 'Select All'}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => this.handleSelectAllSections(!allSelected)}
                      style={{ height: '24px', fontSize: '11px', cursor: 'pointer' }}
                    />
                  </div>
                </div>

                {/* Export Action Buttons */}
                <div className="btn-group mt-2 mt-md-0">
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={this.exportToExcel}
                    disabled={loading || noneSelected}
                    className="mr-2"
                    title={noneSelected ? 'Select at least one section checkbox' : 'Export Selected Sections to Excel'}
                  >
                    Export Excel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={this.exportToPDF}
                    disabled={loading || noneSelected}
                    title={noneSelected ? 'Select at least one section checkbox' : 'Export Selected Sections to PDF'}
                  >
                    Export PDF
                  </Button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="border rounded p-2 bg-light shadow-sm">
                {activeTab === 0 && this.renderFinancialPositionStatement(currData, priorData)}
                {activeTab === 1 && this.renderChangesInEquity(currData, priorData)}
                {activeTab === 2 && this.renderCashFlowStatement(currData, priorData)}
                {activeTab === 3 && this.renderNotesSection(currData, priorData)}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }
}

export default withRouter(BalanceSheetStatement);
